// Extensive EN/TR dictionary for LoftPress.
// Rule: never render a hardcoded user-visible string in components; use t(key).
// If a key is missing during build we log a warning at dev-time (see i18n.jsx).

const EN = {
  // Nav & shared
  "brand.tagline": "API Platform",
  "nav.marketplace": "APIs",
  "nav.docs": "Docs",
  "nav.status": "Status",
  "nav.pricing": "Pricing",
  "nav.signin": "Sign in",
  "nav.getstarted": "Get started",
  "nav.dashboard": "Dashboard",
  "nav.admin": "Admin",
  "nav.logout": "Log out",

  // Landing
  "hero.badge": "Premium API infrastructure",
  "hero.title.a": "One account.",
  "hero.title.b": "Multiple APIs.",
  "hero.title.c": "Built for scale.",
  "hero.subtitle": "Developer and data services operated by LoftPress. Currency, holidays, VAT, email, IBAN, QR, phone, geo, timezone and text utilities — one secure gateway, one meter, one bill.",
  "hero.cta.primary": "Start building free",
  "hero.cta.secondary": "Browse APIs",
  "hero.chip.auth": "Bearer & X-API-Key auth",
  "hero.chip.rate": "Per-second rate limits",
  "hero.chip.sla": "99.9% uptime SLA",
  "hero.base": "Base URL",
  "hero.auth": "Auth",
  "hero.version": "Version",

  "landing.metrics.title": "Live platform metrics",
  "landing.metrics.subtitle": "Real values from our production database — no vanity numbers.",
  "landing.metrics.developers": "Developers",
  "landing.metrics.products": "APIs live",
  "landing.metrics.subscriptions": "Active subscriptions",
  "landing.metrics.requests": "Requests (30d)",
  "landing.metrics.latency": "Median latency",
  "landing.metrics.uptime": "Uptime (30d)",
  "landing.metrics.status": "All systems operational",
  "landing.metrics.live": "Live",

  "landing.products.eyebrow": "Our APIs",
  "landing.products.title": "APIs offered by LoftPress",
  "landing.products.subtitle": "First-party developer and data services operated by LoftPress. Every product ships with a live playground, code snippets in four languages, and predictable pricing.",
  "landing.products.explore": "Explore",
  "landing.products.pro_from": "Pro from",
  "landing.products.coming_soon": "Coming soon",
  "landing.products.attribution": "APIs delivered on LoftPress infrastructure using licensed or open data sources.",

  "landing.pricing.eyebrow": "Pricing",
  "landing.pricing.title": "Simple, transparent pricing",
  "landing.pricing.subtitle": "Start free. Upgrade when you need to. Cancel anytime.",
  "landing.pricing.popular": "Most popular",
  "landing.pricing.trial": "Trial",
  "landing.pricing.pro": "Pro",
  "landing.pricing.enterprise": "Enterprise",
  "landing.pricing.custom": "Custom",
  "landing.pricing.per_month": "/mo",
  "landing.pricing.reqs": "requests",
  "landing.pricing.rate": "req/sec",
  "landing.pricing.feat.trial.keys": "1 API key",
  "landing.pricing.feat.trial.rate": "5 req/second",
  "landing.pricing.feat.trial.support": "Community support",
  "landing.pricing.feat.pro.keys": "5 API keys",
  "landing.pricing.feat.pro.rate": "50 req/second",
  "landing.pricing.feat.pro.sla": "99.9% uptime SLA",
  "landing.pricing.feat.pro.support": "Email support",
  "landing.pricing.feat.ent.keys": "25 API keys",
  "landing.pricing.feat.ent.rate": "250 req/second",
  "landing.pricing.feat.ent.support": "Priority + dedicated support",
  "landing.pricing.feat.ent.audit": "SLA, audit logs, IP allowlist",
  "pricing.tax.suffix": "+ applicable taxes",
  "pricing.tax.note": "VAT, GST or other applicable sales tax is calculated and collected by Creem at checkout based on the customer's location.",

  "landing.trust.eyebrow": "Trust",
  "landing.trust.title": "Trust & security",
  "landing.trust.subtitle": "The controls infrastructure teams expect — nothing less.",
  "landing.trust.hashed.title": "Hashed secrets",
  "landing.trust.hashed.body": "API keys are bcrypt-hashed at rest. Full secret shown only once at creation — you can rotate or revoke anytime.",
  "landing.trust.ipallow.title": "IP allowlisting",
  "landing.trust.ipallow.body": "Restrict any API key to a set of trusted IPs from your dashboard settings.",
  "landing.trust.audit.title": "Audit logs",
  "landing.trust.audit.body": "Every security-sensitive action — key creation, rotation, revocation — is logged with actor and IP.",
  "landing.trust.spend.title": "Spend limits",
  "landing.trust.spend.body": "Set monthly spend and usage limits per account and get alerted before you approach them.",

  "landing.dev.eyebrow": "Developer-first",
  "landing.dev.title": "Developer-first, from day one",
  "landing.dev.subtitle": "One API key, one base URL, one billing surface. Copy, paste, ship.",
  "landing.dev.one_url": "One base URL",
  "landing.dev.one_auth": "Simple auth",
  "landing.dev.one_env": "Standard envelope",
  "landing.dev.one_url_desc": "loftpress.shop/api/v1/{product}",
  "landing.dev.one_auth_desc": "Standard X-API-Key header. Rotate keys with a single click.",
  "landing.dev.one_env_desc": "Every response ships with success, data, request_id, and credits_remaining.",

  "landing.faq.eyebrow": "FAQ",
  "landing.faq.title": "Frequently asked",
  "landing.faq.q1": "Do I need a card to start?",
  "landing.faq.a1": "No. Every account starts on the Trial plan with 500 monthly requests and 500 welcome credits — no card required.",
  "landing.faq.q2": "How is billing structured?",
  "landing.faq.a2": "Every product has a monthly plan (Trial, Pro, Enterprise). Requests are metered against your plan quota. Credits are optional top-ups that never expire and cover per-request compute on top of the quota.",
  "landing.faq.q3": "Can I use one account across all APIs?",
  "landing.faq.a3": "Yes. LoftPress is designed as an account-wide meter: one login, one billing surface, and per-product API keys with independent quotas.",
  "landing.faq.q4": "What SLAs do you offer?",
  "landing.faq.a4": "Pro plans include a 99.9% uptime SLA. Enterprise plans include custom SLAs, dedicated support, and audit logging.",
  "landing.faq.q5": "How do you handle payments?",
  "landing.faq.a5": "Payments are processed by Creem as our Merchant of Record. Card data never touches our servers; every checkout, refund, and invoice is handled directly by Creem with signed webhooks.",
  "landing.faq.q6": "Where does the data come from?",
  "landing.faq.a6": "Currency data is served from LoftPress-managed FX pipelines sourced from public ECB data. Holiday calendars use open holiday datasets. IP Intelligence uses IPinfo.io once we complete the licensed commercial configuration. All APIs are delivered through LoftPress infrastructure — LoftPress operates the endpoints, meters usage, and provides support.",

  // Footer
  "footer.terms": "Terms",
  "footer.privacy": "Privacy",
  "footer.aup": "Acceptable Use",
  "footer.status": "Status",
  "footer.contact": "Contact",
  "footer.tagline": "The LoftPress API platform.",
  "footer.product": "Product",
  "footer.legal": "Legal",
  "footer.company": "Company",
  "seo.landing.title": "APIs for modern operations",
  "seo.landing.desc": "First-party developer APIs from LoftPress: currency FX, holidays, VAT, email, IBAN, QR, phone, geo, timezone and text — one gateway, one bill.",
  "seo.marketplace.title": "API Marketplace",
  "seo.marketplace.desc": "Browse all LoftPress first-party APIs by category — Finance, Utilities, Security.",
  "seo.docs.title": "Documentation",
  "seo.docs.desc": "Getting started, authentication, rate limits, errors and code examples for every LoftPress API.",
  "seo.status.title": "Platform Status",
  "seo.status.desc": "Live operational status for every LoftPress API and 60-day uptime history.",
  "footer.copyright": "All rights reserved.",
  "onboarding.eyebrow": "Get started",
  "onboarding.title": "Make your first API call in 3 steps",
  "onboarding.step1": "Create an API key",
  "onboarding.step1_cta": "Open API keys",
  "onboarding.step2": "Copy the sample request (curl)",
  "onboarding.step2_cta": "Copy sample",
  "onboarding.step3": "Run your first call from the playground",
  "onboarding.step3_cta": "Open playground",
  "onboarding.copied": "Copied — paste it in your terminal",

  // Auth
  "auth.login.title": "Welcome back",
  "auth.login.welcome": "Welcome back",
  "auth.login.subtitle": "Sign in to your LoftPress account.",
  "auth.register.title": "Create your account",
  "auth.register.subtitle": "500 free credits on us — no card required.",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.name": "Full name",
  "auth.submit.login": "Sign in",
  "auth.submit.register": "Create account",
  "auth.forgot": "Forgot password?",
  "auth.have": "Already have an account?",
  "auth.no": "Don’t have an account?",
  "auth.signIn": "Sign in",
  "auth.signUp": "Sign up",
  "auth.forgot.title": "Reset your password",
  "auth.forgot.subtitle": "We’ll send you a link to reset your password. Check your inbox and spam folder.",
  "auth.forgot.submit": "Send reset link",
  "auth.forgot.sent_title": "Check your inbox.",
  "auth.forgot.sent_body": "If we have that email on file, a reset link is on its way.",
  "auth.forgot.back": "Back to sign in",
  "auth.reset.title": "Set a new password",
  "auth.reset.subtitle": "Pick a strong password at least 8 characters long.",
  "auth.reset.submit": "Reset password",
  "auth.reset.missing": "Missing token in URL.",
  "auth.consent.line": "I have read and accept the {links}.",
  "auth.consent.terms": "Terms of Service",
  "auth.consent.privacy": "Privacy Policy",
  "auth.consent.kvkk": "KVKK Notice",
  "auth.consent.subscription": "Subscription Terms",
  "auth.consent.marketing": "I would also like to receive product updates and offers (optional).",
  "auth.consent.required": "You must accept the terms to continue.",
  "auth.dev_hint": "Demo credentials",

  // Dashboard shared
  "dash.overview": "Overview",
  "dash.subs": "Subscriptions",
  "dash.keys": "API keys",
  "dash.usage": "Usage",
  "dash.billing": "Billing",
  "dash.credits": "Credits",
  "dash.settings": "Settings",
  "dash.section.account": "Account",
  "dash.consents": "Consents",

  // Dashboard overview
  "dash.overview.title": "Overview",
  "dash.overview.subtitle": "Everything you need to monitor your APIs at a glance.",
  "dash.overview.stat.credits": "Available credits",
  "dash.overview.stat.credits_hint": "Ledger balance",
  "dash.overview.stat.subs": "Active subscriptions",
  "dash.overview.stat.keys": "API keys",
  "dash.overview.stat.requests": "Requests (30d)",
  "dash.overview.stat.errors": "errors",
  "dash.overview.chart.title": "Daily usage · last 14 days",
  "dash.overview.chart.axis": "requests / day",
  "dash.overview.subs.title": "Active subscriptions",
  "dash.overview.subs.manage": "Manage",
  "dash.overview.subs.empty": "No active subscriptions.",
  "dash.overview.errors.title": "Errors",
  "dash.overview.errors.body": "View detailed logs, filter by product, and drill into individual failures.",
  "dash.overview.errors.view": "View usage logs",
  "dash.overview.browse": "Browse APIs",
  "dash.overview.in_30d": "in 30d",

  // Dashboard subscriptions
  "dash.subs.title": "Subscriptions",
  "dash.subs.subtitle": "Manage your active and past product subscriptions.",
  "dash.subs.empty_title": "You don’t have any subscriptions yet.",
  "dash.subs.browse": "Browse APIs",
  "dash.subs.used": "Used",
  "dash.subs.limit": "Limit",
  "dash.subs.period_end": "Period ends",
  "dash.subs.upgrade": "Upgrade / Change plan",
  "dash.subs.cancel": "Cancel",
  "dash.subs.cancel_note": "Access continues until the period ends. To end immediately use the Creem customer portal.",
  "dash.subs.trial_note": "Free trial ends automatically at the end of the period.",
  "dash.subs.badge.cancels_at_period_end": "Cancels at period end",
  "dash.subs.badge.active": "active",
  "dash.subs.badge.trialing": "trialing",
  "dash.subs.badge.canceled": "canceled",
  "dash.subs.badge.expired": "expired",
  "dash.subs.badge.past_due": "past due",
  "dash.subs.badge.paused": "paused",
  "dash.subs.portal": "Manage billing (Creem portal)",
  "dash.subs.portal_missing": "Complete a paid purchase to unlock the billing portal.",

  // Dashboard API keys
  "dash.keys.title": "API keys",
  "dash.keys.subtitle": "Generate keys per product. Rotate or revoke anytime — the full secret is shown only once.",
  "dash.keys.create": "Create key",
  "dash.keys.no_sub": "You need an active subscription before creating an API key.",
  "dash.keys.browse": "Browse APIs",
  "dash.keys.col.name": "Name",
  "dash.keys.col.product": "Product",
  "dash.keys.col.prefix": "Prefix",
  "dash.keys.col.last_used": "Last used",
  "dash.keys.col.status": "Status",
  "dash.keys.col.actions": "Actions",
  "dash.keys.empty": "No API keys yet.",
  "dash.keys.status.active": "active",
  "dash.keys.status.revoked": "revoked",
  "dash.keys.never": "never",
  "dash.keys.modal.title": "Create API key",
  "dash.keys.modal.product": "Product",
  "dash.keys.modal.product_placeholder": "Select…",
  "dash.keys.modal.name": "Name",
  "dash.keys.modal.name_placeholder": "Production backend",
  "dash.keys.secret.title": "Copy your API key now",
  "dash.keys.secret.body": "This is the only time you’ll see the full secret.",
  "dash.keys.secret.copy": "Copy to clipboard",
  "dash.keys.secret.done": "Done",
  "dash.keys.rotate": "Rotate",
  "dash.keys.revoke": "Revoke",

  // Dashboard usage
  "dash.usage.title": "Usage & requests",
  "dash.usage.subtitle": "Most recent 100 API requests across your account.",
  "dash.usage.col.time": "Time",
  "dash.usage.col.endpoint": "Endpoint",
  "dash.usage.col.status": "Status",
  "dash.usage.col.latency": "Latency",
  "dash.usage.col.credits": "Credits",
  "dash.usage.empty": "No requests yet.",

  // Dashboard billing
  "dash.billing.title": "Order history",
  "dash.billing.subtitle": "Purchase summaries for your records. Official tax invoices and receipts are issued by Creem as Merchant of Record.",
  "dash.billing.mor_notice": "Creem issues the official tax invoice and receipt. LoftPress provides a purchase summary for your records.",
  "dash.billing.col.invoice": "Order",
  "dash.billing.col.date": "Date",
  "dash.billing.col.product": "Product",
  "dash.billing.col.subtotal": "Subtotal",
  "dash.billing.col.tax": "Tax",
  "dash.billing.col.total": "Total",
  "dash.billing.col.amount": "Amount",
  "dash.billing.col.status": "Status",
  "dash.billing.col.provider": "Provider",
  "dash.billing.col.action": "Action",
  "dash.billing.empty": "No orders yet.",
  "dash.billing.download": "Purchase summary",
  "dash.billing.download_error": "Could not download the purchase summary. Please try again.",
  "dash.billing.view": "View",
  "dash.billing.external_receipt": "Official Creem receipt",
  "dash.billing.env.test": "Test",
  "dash.billing.env.live": "Live",
  "dash.billing.type.credits": "Credits",
  "dash.billing.type.subscription": "Subscription",
  "dash.billing.status.paid": "Paid",
  "dash.billing.status.refunded": "Refunded",
  "dash.billing.status.pending": "Pending",
  "dash.billing.status.failed": "Failed",
  "dash.billing.detail.title": "Purchase summary",
  "dash.billing.detail.disclaimer": "This is a purchase summary provided by LoftPress. It is not a tax invoice — Creem issues the official invoice and receipt as Merchant of Record.",
  "dash.billing.detail.qty": "Quantity",
  "dash.billing.detail.unit_price": "Unit price",
  "dash.billing.detail.refunded": "Refunded",
  "dash.billing.detail.net_paid": "Net paid",
  "dash.billing.detail.timeline": "Refund & dispute activity",
  "dash.billing.status.refunded_full": "Refunded",
  "dash.billing.status.refunded_partial": "Partially refunded",
  "dash.billing.status.disputed": "Disputed",
  "dash.billing.status.chargeback": "Chargeback",
  "dash.billing.freeze_banner": "Top-ups are temporarily disabled while a dispute on your account is under review. Please contact support once resolved.",
  "dash.billing.badge.refunded": "REFUNDED",
  "dash.billing.badge.partial": "PARTIAL REFUND",
  "dash.billing.badge.disputed": "DISPUTED",
  "dash.billing.badge.chargeback": "CHARGEBACK",

  // Dashboard credits (top-up)
  "dash.credits.title": "Credits",
  "dash.credits.subtitle": "Credits cover per-request compute on top of your plan quota. They never expire.",
  "dash.credits.balance": "Available balance",
  "dash.credits.topup.title": "Buy credits",
  "dash.credits.topup.subtitle": "Enter any whole-dollar amount and pay securely with Creem.",
  "dash.credits.topup.amount_label": "Amount (USD)",
  "dash.credits.topup.credits_label": "You will receive",
  "dash.credits.topup.bonus": "bonus",
  "dash.credits.topup.rate": "1 USD = {per} credits",
  "dash.credits.topup.integer_only": "Enter a whole-dollar amount (no cents).",
  "dash.credits.topup.range": "Between {min} and {max}",
  "dash.credits.topup.button": "Continue to secure checkout",
  "dash.credits.topup.note": "You will be redirected to Creem to complete the payment. Credits are added only after we verify the webhook.",
  "dash.credits.txs.title": "Transactions",
  "dash.credits.txs.col.date": "Date",
  "dash.credits.txs.col.reason": "Reason",
  "dash.credits.txs.col.delta": "Delta",
  "dash.credits.txs.col.balance": "Balance",
  "dash.credits.txs.empty": "No transactions yet.",

  // Dashboard settings
  "dash.settings.title": "Settings",
  "dash.settings.subtitle": "Webhooks, IP allowlist, spend limits, language.",
  "dash.settings.webhook": "Webhook URL",
  "dash.settings.webhook_placeholder": "https://your-app.com/loftpress/webhook",
  "dash.settings.webhook_hint": "We POST usage & billing events here with a signed payload.",
  "dash.settings.allowlist": "IP allowlist",
  "dash.settings.allowlist_placeholder": "One IP per line — leave empty to allow all",
  "dash.settings.spend_limit": "Monthly spend limit (USD)",
  "dash.settings.language": "Language",
  "dash.settings.lang_en": "English",
  "dash.settings.lang_tr": "Türkçe",
  "dash.settings.alerts": "Email me when I hit 80% of any monthly quota",
  "dash.settings.save": "Save changes",

  // APIs page (formerly labelled "Marketplace"; URL /marketplace kept for continuity)
  "marketplace.title": "APIs offered by LoftPress",
  "marketplace.subtitle": "First-party developer and data services operated by LoftPress. Every API ships with predictable pricing, a live playground, and code snippets in four languages.",
  "marketplace.search": "Search APIs, categories, use cases…",
  "marketplace.all": "All",
  "marketplace.card.trial": "Trial",
  "marketplace.card.pro": "Pro",
  "marketplace.card.ent": "Ent",
  "marketplace.pending_config": "Configuration pending",
  "marketplace.coming_soon": "Coming soon",
  "marketplace.empty": "No APIs match your search.",

  // API detail
  "api.usecases": "Use cases",
  "api.endpoints": "Endpoints",
  "api.base": "Base",
  "api.code_examples": "Code examples",
  "api.sample_response": "Sample response",
  "api.playground.title": "Live playground",
  "api.playground.run": "Run request",
  "api.playground.running": "Running…",
  "api.playground.note": "No API key required. Rate-limited to 30 req/min per IP.",
  "api.playground.placeholder": "// Click Run to try the API",
  "api.plans.title": "Pricing plans",
  "api.plans.subscribe": "Subscribe",
  "api.plans.contact": "Contact sales",
  "api.plans.pending": "Not yet available for purchase",
  "api.plans.pending_body": "This API is currently being configured for commercial availability. Please check back soon.",
  "api.status.operational": "operational",
  "api.status.maintenance": "maintenance",
  "api.status.pending": "pending config",
  "api.status.all_ok": "All systems operational.",
  "api.status.live": "Live status",
  "api.col.method": "Method",
  "api.col.path": "Path",
  "api.col.description": "Description",
  "api.col.credits": "Credits",
  "api.breadcrumb.marketplace": "APIs",
  "api.detail.back": "Back to APIs",
  "api.detail.maintenance": "maintenance",
  "api.detail.operational": "operational",
  "api.detail.coming_soon_title": "Coming soon",
  "api.detail.coming_soon_body": "This API is being finalised for commercial availability. Subscriptions, API-key issuance, and billing are closed until launch.",
  "api.detail.coming_soon_toast": "This API is coming soon and cannot be purchased yet.",
  "api.detail.playground_coming_soon": "This API is coming soon — the live playground is disabled until launch.",
  "api.detail.playground.title": "Live playground",
  "api.detail.playground.run": "Run request",
  "api.detail.playground.running": "Running…",
  "api.detail.playground.hint": "No API key required. Rate-limited to 30 req/min per IP.",
  "api.detail.playground.click_run": "Click Run to try the API",
  "api.detail.plans": "Pricing plans",
  "api.detail.use_cases": "Use cases",
  "api.detail.endpoints": "Endpoints",
  "api.detail.base": "Base",
  "api.detail.method": "Method",
  "api.detail.path": "Path",
  "api.detail.description": "Description",
  "api.detail.credits": "Credits",
  "api.detail.code_examples": "Code examples",
  "api.detail.sample_response": "Sample response",
  "api.detail.changelog_status": "Changelog / Status",
  "api.detail.live_status": "Live status →",
  "docs.eyebrow": "Documentation",
  "docs.guides": "Guides",
  "docs.products": "Products",
  "docs.getting_started.title": "Getting started",
  "docs.getting_started.intro": "LoftPress is a single-account API platform. Each first-party API operated by LoftPress exposes its own endpoints under /api/v1/{product} and is authenticated with a per-product API key issued from your dashboard.",
  "docs.getting_started.step1": "Sign up and subscribe to an API from the LoftPress catalog.",
  "docs.getting_started.step2_pre": "Create an API key from",
  "docs.getting_started.step2_link": "Dashboard → API keys",
  "docs.getting_started.step3": "Copy the secret shown once; it is stored as a bcrypt hash and cannot be recovered later.",
  "docs.getting_started.step4": "Send the secret as the X-API-Key header.",
  "docs.auth.title": "Authentication",
  "docs.auth.body": "All requests to /api/v1/* require an API key. Pass it via the X-API-Key header. Keys are per-product; using a key on the wrong product returns 403 wrong_product.",
  "docs.auth.without_key": "Request without a key",
  "docs.rate.title": "Rate limits",
  "docs.rate.body": "Rate limits are enforced per API key using a per-second sliding window. Limits are defined by your plan:",
  "docs.rate.trial": "Trial",
  "docs.rate.trial_desc": "5 req/sec · 500 req/month · 1 key.",
  "docs.rate.pro": "Pro",
  "docs.rate.pro_desc": "50 req/sec · 100,000 req/month · 5 keys.",
  "docs.rate.ent": "Enterprise",
  "docs.rate.ent_desc": "custom throughput & keys.",
  "docs.rate.exceed": "Requests exceeding the per-second limit return 429 rate_limited.",
  "docs.errors.title": "Error codes",
  "docs.errors.code": "Code",
  "docs.errors.meaning": "Meaning",
  "docs.errors.401_missing": "Header X-API-Key not provided",
  "docs.errors.401_invalid": "Key is revoked or unknown",
  "docs.errors.402_quota": "Monthly plan quota reached",
  "docs.errors.402_sub": "Subscription canceled or past due",
  "docs.errors.403_wrong": "Key issued for a different product",
  "docs.errors.403_ip": "Client IP not on allowlist",
  "docs.errors.429": "Per-second rate limit exceeded",
  "docs.errors.503": "Service in scheduled maintenance",
  "docs.webhooks.title": "Webhooks",
  "docs.webhooks.body1_pre": "Configure a webhook URL from",
  "docs.webhooks.body1_link": "Dashboard → Settings",
  "docs.webhooks.body1_post": "LoftPress will POST usage & billing events to your endpoint with a signed X-LoftPress-Signature header.",
  "docs.webhooks.body2": "Payment webhooks are exposed at /api/billing/webhook/{provider}. Signature verification is provider-specific and must be enabled before going live in production.",
  "docs.sdk.title": "Code examples",
  "status.eyebrow": "Status",
  "status.title": "Platform status",
  "status.all_operational": "All systems operational",
  "status.some_degraded": "Some services degraded",
  "status.services": "Services",
  "status.uptime_60d": "60-day uptime",
  "status.60d_ago": "60 days ago",
  "status.today": "today",
  "status.recent_incidents": "Recent incidents",
  "status.no_incidents": "No incidents on record.",
  "status.chip.operational": "operational",
  "status.chip.maintenance": "maintenance",
  "status.chip.down": "down",
  "status.chip.degraded": "degraded",
  "api.detail.pending_title": "Not yet available for purchase",
  "api.detail.pending_body": "This API is currently being configured for commercial availability. Please check back soon.",
  "api.detail.pending_toast": "This API is not yet available for purchase.",
  "api.detail.contact_sales": "Contact sales",

  // Checkout redirect
  "checkout.redirecting": "Redirecting to secure checkout…",

  // Payment result pages
  "payment.success.title": "Payment successful",
  "payment.success.body": "Thank you for your purchase. Your access will be activated within a few seconds after we verify the payment.",
  "payment.success.cta": "Open dashboard",
  "payment.cancel.title": "Payment canceled",
  "payment.cancel.body": "No charge was made. You can try again from the credit or plan page.",
  "payment.cancel.cta": "Back to dashboard",
  "payment.pending.title": "Payment is being processed",
  "payment.pending.body": "We are confirming your payment. This can take up to a minute.",
  "payment.pending.cta": "Refresh dashboard",

  // Cookie banner
  "cookie.title": "We use essential cookies",
  "cookie.body": "We use strictly necessary cookies for authentication and, with your consent, optional analytics cookies to improve the product.",
  "cookie.accept_all": "Accept all",
  "cookie.only_necessary": "Only necessary",
  "cookie.manage": "Manage preferences",

  // Docs
  "docs.eyebrow": "Documentation",
  "docs.guides": "Guides",
  "docs.products": "Products",
  "docs.getting_started": "Getting started",
  "docs.auth": "Authentication",
  "docs.rate_limits": "Rate limits",
  "docs.errors": "Error codes",
  "docs.webhooks": "Webhooks",
  "docs.sdk": "Code examples",

  // Status
  "status.eyebrow": "Status",
  "status.title": "Platform status",
  "status.all_ok": "All systems operational",
  "status.degraded": "Some services degraded",
  "status.services": "Services",
  "status.history_title": "60-day uptime",
  "status.days_ago": "60 days ago",
  "status.today": "today",
  "status.incidents_title": "Recent incidents",
  "status.incidents_empty": "No incidents on record.",

  // Legal
  "legal.eyebrow": "Legal",
  "legal.pending": "This document has not been published yet. Please check back soon.",
  "legal.home": "Home",
  "legal.version": "Version",
  "legal.updated": "Last updated",
  "legal.print": "Print",
  "legal.toc": "On this page",
  "legal.support": "Support",
  "contact.form.title": "Send us a message",
  "contact.form.name": "Full name",
  "contact.form.email": "Email address",
  "contact.form.category": "Subject",
  "contact.form.order": "Order ID (optional)",
  "contact.form.order_ph": "e.g. ord_abc123",
  "contact.form.message": "Your message",
  "contact.form.submit": "Send message",
  "contact.form.ack.pre": "I have read and understood the",
  "contact.form.ack.privacy": "Privacy Policy",
  "contact.form.note": "We reply within 3 business days (Mon–Fri, 09:00–17:00 UK time). Please do not paste passwords or API keys.",
  "contact.form.success": "Message sent. We'll get back to you shortly.",
  "contact.form.error": "We couldn't send your message. Please try again in a moment.",
  "contact.form.thanks": "Thanks — your message is on its way to our team. We reply within 3 business days.",
  "contact.cat.sales": "Sales & pricing",
  "contact.cat.technical": "Technical support",
  "contact.cat.billing": "Billing / refund",
  "contact.cat.privacy": "Privacy / data request",
  "contact.cat.legal": "Legal notice",
  "contact.cat.security": "Security report",
  "contact.cat.other": "Other",
  "checkout.mor.notice": "Payment processed by Creem as Merchant of Record.",
  "checkout.mor.link": "Creem Buyer Terms & Privacy",
  "checkout.mor.notice_short": "Payment: Creem MoR",
  "checkout.consent.required": "Please accept the Terms and Privacy Policy first.",
  "checkout.consent.terms.pre": "I have read and accept the",
  "checkout.consent.terms.link": "Terms of Service",
  "checkout.consent.and": "and",
  "checkout.consent.privacy.link": "Privacy Policy",
  "api.detail.confirm.body": "Please confirm the subscription. The payment will be processed by Creem, which acts as our Merchant of Record.",
  "api.detail.confirm.cta": "Continue to Creem",
  "common.cancel": "Cancel",
  "footer.cookie_prefs": "Cookie preferences",
  "footer.refund": "Refunds & cancellation",

  // Admin
  "admin.section.admin": "Admin",
  "admin.section.overview": "Overview",
  "admin.section.customers": "Customers",
  "admin.section.commerce": "Commerce",
  "admin.section.operations": "Operations",
  "admin.section.security": "Security",
  "admin.section.config": "Configuration",
  "admin.overview": "Overview",
  "admin.analytics": "Analytics",
  "admin.users": "Users",
  "admin.bulk": "Bulk operations",
  "admin.products": "Products",
  "admin.products_v2": "Products",
  "admin.products_legacy": "Products (legacy)",
  "admin.plans": "Plans",
  "admin.plans_v2": "Plans",
  "admin.plans_legacy": "Plans (legacy)",
  "admin.reconciliation": "Reconciliation",
  "admin.credit_packs": "Credit packs",
  "admin.incidents": "Incidents",
  "admin.settings": "Settings",
  "adminp4.title": "Security & Operations",
  "adminp4.subtitle": "2FA, security events, notifications, typed settings, health and incidents.",
  "adminp4.tab.twofa": "2FA",
  "adminp4.tab.security": "Security",
  "adminp4.tab.notifications": "Notifications",
  "adminp4.tab.settings": "Settings",
  "adminp4.tab.health": "Health",
  "adminp4.tab.incidents": "Incidents",
  "adminp4.twofa.title": "Two-factor authentication",
  "adminp4.twofa.enabled": "Two-factor authentication enabled",
  "admin.creem": "Creem settings",
  "admin.creem_products": "Creem product sync",
  "admin.resources": "API resources",
  "admin.company": "Company info",
  "admin.legal": "Legal texts",
  "admin.consents": "Consent records",
  // adminp.products.*
  "adminp.products.title": "Products",
  "adminp.products.subtitle": "Enterprise CRUD with immutable version history, publish/rollback and reveal-once secret rotation.",
  "adminp.products.new": "New product",
  "adminp.products.created": "Product created",
  "adminp.products.search_ph": "Search by slug, name or description…",
  "adminp.products.all_statuses": "All statuses",
  "adminp.products.total": "products",
  "adminp.products.empty": "No products match the current filters.",
  "adminp.products.col.name": "Name",
  "adminp.products.col.slug": "Slug",
  "adminp.products.col.status": "Status",
  "adminp.products.col.version": "Version",
  "adminp.products.col.updated": "Updated",
  "adminp.products.publish": "Publish",
  "adminp.products.archive": "Archive",
  "adminp.products.reactivate": "Reactivate",
  "adminp.products.rotate_secret": "Rotate secret",
  "adminp.products.rotate": "Rotate",
  "adminp.products.rotate_warning": "The new secret is shown only once. Copy it now — it cannot be viewed again.",
  "adminp.products.versions": "Versions",
  "adminp.products.secrets": "Secrets",
  "adminp.products.no_secrets": "No active secrets.",
  // adminp.plans.*
  "adminp.plans.title": "Plans",
  "adminp.plans.subtitle": "Immutable plan versioning. Existing subscribers keep their snapshot; new subscribers get the current published version.",
  "adminp.plans.new": "New plan",
  "adminp.plans.created": "Plan created",
  "adminp.plans.tier_name_required": "Tier and display name are required",
  "adminp.plans.total": "plans",
  "adminp.plans.empty": "No plans match the current filters.",
  "adminp.plans.col.tier": "Tier",
  "adminp.plans.col.name": "Display name",
  "adminp.plans.col.status": "Status",
  "adminp.plans.col.version": "Version",
  "adminp.plans.col.price": "Price",
  "adminp.plans.col.effective": "Effective",
  "adminp.plans.versions": "Versions",
  "adminp.plans.draft_version": "Draft version",
  "adminp.plans.publish_reason_prompt": "Publish this version — reason (audit-logged)",
  "adminp.plans.rollback_reason_prompt": "Rollback creates a NEW version with the historical snapshot — reason (audit-logged)",
  "adminp.plans.draft_snapshot_prompt": "Draft snapshot JSON (partial values allowed)",
  "adminp.plans.draft_reason_prompt": "Reason for creating this draft (audit-logged)",
  // adminp.analytics.*
  "adminp.analytics.title": "Analytics",
  "adminp.analytics.subtitle": "Executive analytics. Only real values from the database — no vanity numbers.",
  "adminp.analytics.gross": "Gross",
  "adminp.analytics.refunded": "Refunded",
  "adminp.analytics.disputed": "Disputed",
  "adminp.analytics.net": "Net",
  "adminp.analytics.mrr": "MRR",
  "adminp.analytics.arr": "ARR",
  "adminp.analytics.arpu": "ARPU",
  "adminp.analytics.churn": "Churn",
  "adminp.analytics.credits_sold": "Credits sold",
  "adminp.analytics.credits_spent": "Credits spent",
  "adminp.analytics.liability": "Credit liability",
  "adminp.analytics.api_success": "API success",
  "adminp.analytics.trend": "Daily gross trend",
  "adminp.analytics.top_products": "Top products",
  "adminp.analytics.top_customers": "Top customers",
  "adminp.analytics.top_plans": "Top plans",
  // adminp.bulk.*
  "adminp.bulk.title": "Bulk operations",
  "adminp.bulk.subtitle": "CSV-driven mass operations with dry-run and idempotency. Invalid rows are skipped; only valid rows are applied.",
  "adminp.bulk.dry_run": "Dry-run",
  "adminp.bulk.execute": "Execute batch",
  "adminp.bulk.template": "Download template",
  "adminp.bulk.batches": "Recent batches",
  "adminp.bulk.reason_required": "Batch reason is required (min 3 chars)",
  "admin.refunds": "Refunds & disputes",
  "admin.webhooks": "Webhook events",
  "admin.cases": "Cases",
  "admin.audit": "Audit log",
  "admin.cmdk.placeholder": "Search anything — users, orders, refunds…",
  "admin.cmdk.hint": "Type ≥ 2 chars. Enter to open · ↑↓ to navigate · Esc to close.",
  "admin.cmdk.no_results": "No matches.",
  "admin.recon.title": "Financial reconciliation",
  "admin.recon.subtitle": "Read-only diagnostic. Never mutates. Any fixes require Super Admin + dry-run.",
  "admin.recon.drift": "Ledger drift users",
  "admin.recon.orphan": "Orphan invoices",
  "admin.recon.violations": "Refund-cap violations",
  "admin.recon.stale_freezes": "Stale freezes",
  "admin.recon.failed_hooks": "Failed webhooks",
  "admin.recon.overall": "Overall",
  "admin.recon.drift_users": "Users with ledger vs balance drift",
  "admin.recon.orphan_list": "Credit invoices with no matching ledger row",
  "admin.recon.violations_list": "Invoices where reversed credits > granted",
  "admin.sessions": "Admin sessions",
  "admin.sessions.title": "Admin session manager",
  "admin.sessions.subtitle": "Every active admin session, with server-side revoke and audit.",
  "admin.sessions.search_ph": "Search by email, IP, browser…",
  "admin.sessions.active_only": "Active only",
  "admin.sessions.suspicious_only": "Suspicious only",
  "admin.sessions.total": "sessions",
  "admin.sessions.empty": "No sessions match the current filters.",
  "admin.sessions.this_device": "This device",
  "admin.sessions.col.admin": "Admin",
  "admin.sessions.col.role": "Role",
  "admin.sessions.col.device": "Device",
  "admin.sessions.col.login": "Logged in",
  "admin.sessions.col.activity": "Last activity",
  "admin.sessions.col.expires": "Expires",
  "admin.sessions.col.status": "Status",
  "admin.sessions.col.actions": "Actions",
  "admin.sessions.revoke": "Revoke",
  "admin.sessions.revoke_title": "Revoke this session",
  "admin.sessions.self_confirm": "I understand this will sign me out of my current device.",
  "admin.sessions.revoked": "Session revoked",
  "admin.sessions.revoked_plural": "sessions revoked",
  "admin.sessions.revoke_others": "Revoke my other sessions",
  "admin.sessions.revoke_others_title": "Revoke all other sessions",
  "admin.sessions.revoke_others_desc": "Every session on your account except this device will be signed out.",
  "admin.sessions.force_logout": "Force-logout admin",
  "admin.sessions.force_logout_title": "Force-logout every session for this admin",
  "admin.sessions.force_logout_done": "Admin sessions terminated",
  "admin.sessions.flag": "Flag suspicious",
  "admin.sessions.flag_title": "Flag this session as suspicious",
  "admin.sessions.flagged": "Session flagged",
  "admin.recon.run_now": "Run new scan",
  "admin.recon.findings_open": "Open findings",
  "admin.recon.findings_critical": "Critical findings",
  "admin.recon.requests_pending": "Pending approvals",
  "admin.recon.requests_executed": "Executed",
  "admin.recon.last_run": "Last run",
  "admin.recon.tab.overview": "Overview",
  "admin.recon.tab.findings": "Findings",
  "admin.recon.tab.approvals": "Pending approvals",
  "admin.recon.search_ph": "Search by email…",
  "admin.recon.filter.all_severity": "All severities",
  "admin.recon.filter.open": "Open only",
  "admin.recon.filter.resolved": "Resolved only",
  "admin.recon.filter.all": "All",
  "admin.recon.findings_total": "findings",
  "admin.recon.empty_findings": "No findings match the current filters.",
  "admin.recon.col.user": "User",
  "admin.recon.col.stored": "Stored balance",
  "admin.recon.col.ledger": "Ledger balance",
  "admin.recon.col.drift": "Drift",
  "admin.recon.col.severity": "Severity",
  "admin.recon.col.source": "Suspected source",
  "admin.recon.col.status": "Status",
  "admin.recon.col.detected": "First detected",
  "admin.recon.dry_run": "Dry-run",
  "admin.recon.request_resolution": "Request correction",
  "admin.recon.resolution_request_title": "Create correction request",
  "admin.recon.proposed": "Proposed",
  "admin.recon.request_action": "Submit request",
  "admin.recon.request_created": "Correction request created — awaiting first approval",
  "admin.recon.finding_for": "Finding for",
  "admin.recon.active_request": "Active resolution request",
  "admin.recon.investigations": "Investigations",
  "admin.recon.approve_title": "Approve correction request",
  "admin.recon.approve": "Approve",
  "admin.recon.approved": "Approval recorded",
  "admin.recon.reject_title": "Reject correction request",
  "admin.recon.reject": "Reject",
  "admin.recon.rejected": "Rejected",
  "admin.recon.execute_title": "Execute compensating adjustment",
  "admin.recon.execute": "Execute",
  "admin.recon.executed": "Compensating ledger entry written",
  "admin.recon.execute_ack": "I understand this will insert a new immutable compensating ledger entry and cannot be undone.",
  "admin.recon.no_pending": "No pending correction requests.",
  "admin.dashboard.title": "Executive dashboard",
  "admin.dashboard.range": "Range",
  "admin.dashboard.days_abbrev": "d",
  "admin.dashboard.revenue_trend": "Revenue trend",
  "admin.kpi.gross": "Gross revenue",
  "admin.kpi.refunded": "Refunded",
  "admin.kpi.net": "Net revenue",
  "admin.kpi.active_subs": "Active subs",
  "admin.kpi.users_total": "Users",
  "admin.kpi.new": "new",
  "admin.kpi.paying": "Paying users",
  "admin.kpi.credits_sold": "Credits sold",
  "admin.kpi.spent": "spent",
  "admin.kpi.credits_outstanding": "Credit liability",
  "admin.kpi.open_disputes": "Open disputes",
  "admin.kpi.open_reviews": "Manual reviews",
  "admin.kpi.failed_webhooks": "Failed webhooks",
  "admin.kpi.frozen_users": "Frozen accounts",
  "admin.action.reviews": "Pending manual reviews",
  "admin.action.disputes": "Open disputes",
  "admin.action.failed_webhooks": "Failed webhooks",
  "admin.action.frozen": "Frozen top-up accounts",
  "admin.action.empty": "All clear.",
  "admin.cases.title": "Refund, dispute & manual review center",
  "admin.cases.subtitle": "Approve refunds that hit an insufficient balance, resolve disputes and freeze/unfreeze accounts.",
  "admin.cases.tab.open": "Open",
  "admin.cases.tab.resolved": "Resolved",
  "admin.cases.tab.all": "All",
  "admin.cases.col.at": "Opened",
  "admin.cases.col.kind": "Kind",
  "admin.cases.col.user": "User",
  "admin.cases.col.invoice": "Order",
  "admin.cases.col.amount": "Amount",
  "admin.cases.col.needed_credits": "Credits to reverse",
  "admin.cases.col.status": "Status",
  "admin.cases.empty": "No cases yet.",
  "admin.cases.approve": "Approve",
  "admin.cases.dismiss": "Dismiss",
  "admin.cases.resolve": "Resolve",
  "admin.cases.approve_title": "Approve refund reversal",
  "admin.cases.approve_desc": "This atomically deducts credits via the immutable ledger.",
  "admin.cases.dismiss_title": "Dismiss review case",
  "admin.cases.dismiss_desc": "The case will be closed without applying a credit reversal.",
  "admin.cases.resolve_title": "Resolve dispute",
  "admin.cases.resolve_desc": "Choose the outcome. On WON the top-up freeze lifts if no other open dispute exists.",
  "admin.cases.reason": "Reason (required for the audit log)",
  "admin.cases.cancel": "Cancel",
  "admin.cases.confirm_approve": "Approve reversal",
  "admin.cases.confirm_dismiss": "Dismiss case",
  "admin.cases.won": "Won",
  "admin.cases.lost": "Lost",
  "admin.cases.approved": "Reversal applied",
  "admin.cases.dismissed": "Case dismissed",
  "admin.cases.dispute_resolved": "Dispute resolved",
  "admin.webhooks.filter.all": "All",
  "admin.webhooks.filter.failed": "Failed only",
  "admin.webhooks.filter.processed": "Processed only",
  "admin.webhooks.filter.type": "Event type…",
  "admin.webhooks.detail": "Webhook event",
  "admin.webhooks.confirm_retry": "Mark this event as unprocessed so Creem can safely redeliver it? (Signature stays intact; idempotency guards duplicates.)",
  "admin.webhooks.retry_requested": "Retry requested",
  "admin.webhooks.redaction_notice": "Sensitive fields are always redacted before display.",
  "admin.audit.title": "Audit log",
  "admin.audit.subtitle": "Immutable trail of every admin mutation.",
  "admin.audit.filter_action": "Filter by action prefix (e.g. refund.)…",
  "admin.audit.col.at": "When",
  "admin.audit.col.actor": "Actor",
  "admin.audit.col.action": "Action",
  "admin.audit.col.target": "Target",
  "admin.users.title": "Users",
  "admin.users.subtitle": "Search, filter, review and act on every account.",
  "admin.users.search_ph": "Search by email, name, or user id…",
  "admin.users.csv": "Export CSV",
  "admin.users.total": "results",
  "admin.users.empty": "No users match.",
  "admin.users.suspended": "SUSPENDED",
  "admin.users.frozen": "FROZEN",
  "admin.users.filter.all": "All",
  "admin.users.filter.paying": "Paying",
  "admin.users.filter.frozen": "Frozen",
  "admin.users.filter.suspended": "Suspended",
  "admin.users.filter.admin": "Admins",
  "admin.users.col.user": "User",
  "admin.users.col.credits": "Credits",
  "admin.users.col.lifetime": "Lifetime",
  "admin.users.col.orders": "Orders",
  "admin.users.col.status": "Status",
  "admin.users.col.risk": "Risk",
  "admin.users.col.joined": "Joined",
  "admin.customer.back": "Back to users",
  "admin.customer.credit_adjust": "Adjust credits",
  "admin.customer.freeze": "Freeze top-up",
  "admin.customer.unfreeze": "Unfreeze top-up",
  "admin.customer.suspend": "Suspend account",
  "admin.customer.unsuspend": "Unsuspend account",
  "admin.customer.k.credits": "Credits",
  "admin.customer.k.gross": "Lifetime gross",
  "admin.customer.k.refunded": "Refunded",
  "admin.customer.k.net": "Net paid",
  "admin.customer.k.orders": "Orders",
  "admin.customer.k.disputes": "Open disputes",
  "admin.customer.tab.overview": "Overview",
  "admin.customer.tab.credits": "Credits",
  "admin.customer.tab.orders": "Orders",
  "admin.customer.tab.subscriptions": "Subscriptions",
  "admin.customer.tab.api_keys": "API keys",
  "admin.customer.tab.refunds": "Refunds",
  "admin.customer.tab.disputes": "Disputes",
  "admin.customer.tab.notes": "Notes",
  "admin.customer.tab.audit": "Audit",
  "admin.customer.overview.joined": "Joined",
  "admin.customer.overview.language": "Language",
  "admin.customer.overview.freeze_reason": "Freeze reason",
  "admin.customer.credits_desc": "Current balance:",
  "admin.customer.notes.add": "Add note",
  "admin.customer.notes.body": "Note",
  "admin.customer.notes.added": "Note added",
  "admin.customer.freeze_done": "Top-up frozen",
  "admin.customer.unfreeze_done": "Top-up unfrozen",
  "admin.customer.suspend_done": "Account suspended",
  "admin.customer.unsuspend_done": "Account unsuspended",
  "admin.customer.delta": "Delta (positive to grant, negative to deduct)",
  "admin.customer.category": "Category",
  "admin.customer.note": "Note (optional)",
  "admin.customer.reason": "Reason (required, audit-logged)",
  "admin.customer.cancel": "Cancel",
  "admin.customer.confirm": "Apply",
  "admin.customer.dialog.credit": "Adjust credit balance",
  "admin.customer.dialog.freeze": "Freeze top-up",
  "admin.customer.dialog.unfreeze": "Unfreeze top-up",
  "admin.customer.dialog.suspend": "Suspend account",
  "admin.customer.dialog.unsuspend": "Unsuspend account",
  "admin.customer.dialog.note": "Add internal note",

  // Admin overview
  "admin.overview.title": "Admin overview",
  "admin.overview.subtitle": "Platform health, revenue, usage — at a glance.",
  "admin.stat.users": "Users",
  "admin.stat.products": "Products",
  "admin.stat.subs": "Active subs",
  "admin.stat.requests": "Requests (30d)",
  "admin.stat.errors": "errors",
  "admin.stat.revenue30": "Revenue (30d)",
  "admin.stat.revenue_total": "Revenue total",

  // Admin users
  "admin.users.title": "Users",
  "admin.users.search": "Search email or name…",
  "admin.users.col.name": "Name",
  "admin.users.col.email": "Email",
  "admin.users.col.role": "Role",
  "admin.users.col.credits": "Credits",
  "admin.users.col.joined": "Joined",
  "admin.users.col.actions": "Actions",
  "admin.users.grant": "Grant credits",
  "admin.users.make_admin": "Make admin",
  "admin.users.revoke_admin": "Revoke admin",
  "admin.users.prompt_credits": "Add how many credits?",

  // Admin products
  "admin.products.title": "Products",
  "admin.products.subtitle": "Toggle active/inactive, enable maintenance mode. Sale-ready state depends on resources + docs + plans.",
  "admin.products.active": "active",
  "admin.products.inactive": "inactive",
  "admin.products.maint": "maintenance",
  "admin.products.normal": "normal",
  "admin.products.sale_ready": "sale-ready",
  "admin.products.pending": "pending config",
  "admin.products.refresh": "Refresh readiness",

  // Admin plans
  "admin.plans.title": "Plans & pricing",
  "admin.plans.subtitle": "All pricing, quota, and rate-limit values are editable.",
  "admin.plans.col.product": "Product",
  "admin.plans.col.tier": "Tier",
  "admin.plans.col.price": "Price",
  "admin.plans.col.reqs": "Monthly req",
  "admin.plans.col.rate": "Rate/s",
  "admin.plans.col.keys": "Keys",

  // Admin credit packs (legacy, retained)
  "admin.packs.title": "Credit packs",
  "admin.packs.subtitle": "Adjust pricing and volume for legacy fixed credit packs (kept for reporting; users buy custom-amount top-ups).",
  "admin.packs.col.name": "Name",
  "admin.packs.col.credits": "Credits",
  "admin.packs.col.price": "Price",
  "admin.packs.col.popular": "Popular",
  "admin.packs.col.active": "Active",

  // Admin incidents
  "admin.incidents.title": "Incidents & maintenance",
  "admin.incidents.subtitle": "Publish updates to the public status page.",
  "admin.incidents.form.title": "Title",
  "admin.incidents.form.affected": "Affected services (comma-sep slugs)",
  "admin.incidents.form.body": "Update body…",
  "admin.incidents.form.publish": "Publish incident",
  "admin.incidents.resolve": "Resolve",
  "admin.incidents.empty": "No incidents on file.",

  // Admin Creem
  "admin.creem.title": "Creem settings",
  "admin.creem.subtitle": "Server-side integration. API keys are stored encrypted at rest and never exposed to the frontend.",
  "admin.creem.mode": "Mode",
  "admin.creem.mode.test": "Test",
  "admin.creem.mode.live": "Live",
  "admin.creem.test_key": "Test API key",
  "admin.creem.live_key": "Live API key",
  "admin.creem.webhook_test": "Test webhook secret",
  "admin.creem.webhook_live": "Live webhook secret",
  "admin.creem.webhook_url": "Webhook URL",
  "admin.creem.success_url": "Success URL",
  "admin.creem.cancel_url": "Cancel URL",
  "admin.creem.currency": "Default currency",
  "admin.creem.tax_mode": "Tax mode",
  "admin.creem.tax_category": "Tax category",
  "admin.creem.min_topup": "Min top-up (USD)",
  "admin.creem.max_topup": "Max top-up (USD)",
  "admin.creem.rate": "USD → credits rate",
  "admin.creem.bonus": "Bonus tiers (JSON)",
  "admin.creem.portal": "Enable customer portal",
  "admin.creem.test_conn": "Test connection",
  "admin.creem.save": "Save settings",
  "admin.creem.masked_notice": "Existing secret is stored encrypted; type a new value to replace it or leave blank to keep it.",
  "admin.creem.status.test": "Test key",
  "admin.creem.status.live": "Live key",
  "admin.creem.status.webhook_test": "Test webhook",
  "admin.creem.status.webhook_live": "Live webhook",
  "admin.creem.status.company": "Company info",
  "admin.creem.status.set": "Set",
  "admin.creem.status.missing": "Missing",
  "admin.creem.status.complete": "Complete",
  "admin.creem.last_test": "Last successful test",
  "admin.creem.last_webhook": "Last webhook received",
  "admin.creem.last_payment": "Last successful payment",
  "admin.creem.last_error": "Last integration error",

  // Admin API resources
  "admin.resources.title": "API resources",
  "admin.resources.subtitle": "External provider configuration. All secrets are encrypted at rest and never returned to the frontend.",
  "admin.resources.add": "Add resource",
  "admin.resources.name": "Name",
  "admin.resources.product": "API product",
  "admin.resources.base_url": "Base URL",
  "admin.resources.test_url": "Test URL",
  "admin.resources.api_key": "API key",
  "admin.resources.secret": "Secret",
  "admin.resources.username": "Username",
  "admin.resources.password": "Password",
  "admin.resources.headers": "Extra headers (JSON)",
  "admin.resources.timeout": "Timeout (s)",
  "admin.resources.retries": "Retries",
  "admin.resources.cache": "Cache (s)",
  "admin.resources.daily": "Daily quota",
  "admin.resources.monthly": "Monthly quota",
  "admin.resources.cost": "Provider cost (USD)",
  "admin.resources.commercial": "Commercial use permitted",
  "admin.resources.resale": "Resale/redistribution allowed",
  "admin.resources.attribution": "Attribution text (if required)",
  "admin.resources.license": "License note",
  "admin.resources.provider_key": "Provider adapter key",
  "admin.resources.active": "Active",
  "admin.resources.save": "Save",
  "admin.resources.test": "Test connection",
  "admin.resources.delete": "Delete",
  "admin.resources.last_ok": "Last healthy",
  "admin.resources.last_err": "Last error",
  "admin.resources.empty": "No API resources yet. Add one to make its API sale-ready.",

  // Admin Creem products
  "admin.creem_products.title": "Creem product sync",
  "admin.creem_products.subtitle": "Link each LoftPress plan and the credit top-up SKU to its Creem product.",
  "admin.creem_products.plan_col.product": "API",
  "admin.creem_products.plan_col.tier": "Tier",
  "admin.creem_products.plan_col.price": "Price",
  "admin.creem_products.plan_col.test_id": "Creem product ID (test)",
  "admin.creem_products.plan_col.live_id": "Creem product ID (live)",
  "admin.creem_products.topup_title": "Credit top-up SKU",
  "admin.creem_products.list": "Fetch Creem products",

  // Admin company
  "admin.company.title": "Company information",
  "admin.company.subtitle": "These fields power every legal page footer, invoice, and KVKK notice. Complete them before publishing legal texts.",
  "admin.company.legal_name": "Legal name",
  "admin.company.number": "Company registration number",
  "admin.company.address": "Registered address",
  "admin.company.country": "Country",
  "admin.company.support": "Support email",
  "admin.company.legal_email": "Legal notice email",
  "admin.company.privacy": "Privacy / KVKK email",
  "admin.company.phone": "Phone",
  "admin.company.authorized": "Authorized signatory",
  "admin.company.tax": "Tax identification number",
  "admin.company.tax_office": "Tax office",
  "admin.company.refund_contact": "Refund contact",
  "admin.company.website": "Website",

  // Admin legal
  "admin.legal.title": "Legal texts",
  "admin.legal.subtitle": "Manage bilingual legal documents. Publish and version any change so consent records remain traceable.",
  "admin.legal.title_en": "Title (English)",
  "admin.legal.title_tr": "Title (Türkçe)",
  "admin.legal.body_en": "Body (English)",
  "admin.legal.body_tr": "Body (Türkçe)",
  "admin.legal.published": "Published on public site",
  "admin.legal.bump": "Bump version (creates a new snapshot; forces re-consent)",
  "admin.legal.save": "Save",
  "admin.legal.versions": "Versions",
  "admin.legal.current_version": "Current version",

  // Admin consents
  "admin.consents.title": "User consent records",
  "admin.consents.subtitle": "Every accepted legal document is stored with an immutable hash. This ledger is your compliance evidence.",
  "admin.consents.col.user": "User",
  "admin.consents.col.doc": "Document",
  "admin.consents.col.version": "Version",
  "admin.consents.col.lang": "Lang",
  "admin.consents.col.context": "Context",
  "admin.consents.col.ip": "IP",
  "admin.consents.col.at": "Accepted at",

  // Admin refunds
  "admin.refunds.title": "Refunds & disputes",
  "admin.refunds.subtitle": "Refund and chargeback events forwarded by Creem webhooks.",
  "admin.refunds.col.at": "Date",
  "admin.refunds.col.user": "User",
  "admin.refunds.col.amount": "Amount",
  "admin.refunds.col.status": "Status",
  "admin.refunds.col.ref": "Creem ref",
  "admin.refunds.col.reason": "Reason",
  "admin.refunds.empty": "No refunds recorded yet.",

  // Admin webhook events
  "admin.webhooks.title": "Creem webhook events",
  "admin.webhooks.subtitle": "All incoming Creem webhooks with signature status and idempotency tracking.",
  "admin.webhooks.col.at": "Received",
  "admin.webhooks.col.type": "Event type",
  "admin.webhooks.col.id": "Event ID",
  "admin.webhooks.col.verified": "Signature",
  "admin.webhooks.col.processed": "Processed",
  "admin.webhooks.col.error": "Error",
  "admin.webhooks.reprocess": "Re-run",

  // Buttons and states
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.copied_toast": "Copied to clipboard",
  "common.revoke": "Revoke",
  "common.rotate": "Rotate",
  "common.create": "Create",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.upgrade": "Upgrade",
  "common.subscribe": "Subscribe",
  "common.select": "Select",
  "common.loading": "Loading…",
  "common.buy": "Buy",
  "common.download": "Download",
  "common.yes": "yes",
  "common.no": "no",
  "common.saved": "Saved",
  "common.updated": "Updated",
  "common.deleted": "Deleted",
  "common.error_generic": "Something went wrong. Please try again.",
  "common.actions": "Actions",

  // Errors
  "err.404.title": "Page not found",
  "err.404.body": "The page you’re looking for doesn’t exist.",
  "err.500.title": "Something broke on our side",
  "err.500.body": "We’re looking into it. Please try again in a few minutes.",
  "err.back_home": "Back home",
};

const TR = {
  // Nav & shared
  "brand.tagline": "API Platformu",
  "nav.marketplace": "API'lar",
  "nav.docs": "Dokümanlar",
  "nav.status": "Durum",
  "nav.pricing": "Fiyatlandırma",
  "nav.signin": "Giriş yap",
  "nav.getstarted": "Başla",
  "nav.dashboard": "Panel",
  "nav.admin": "Yönetim",
  "nav.logout": "Çıkış",

  "hero.badge": "Premium API altyapısı",
  "hero.title.a": "Tek hesap.",
  "hero.title.b": "Çoklu API.",
  "hero.title.c": "Ölçek için tasarlandı.",
  "hero.subtitle": "Loft Press tarafından işletilen geliştirici ve veri servisleri. Kur, tatiller, KDV, e-posta, IBAN, QR, telefon, coğrafya, zaman dilimi ve metin araçları — tek güvenli gateway, tek sayaç, tek fatura.",
  "hero.cta.primary": "Ücretsiz başla",
  "hero.cta.secondary": "API'ları keşfet",
  "hero.chip.auth": "Bearer & X-API-Key kimlik doğrulama",
  "hero.chip.rate": "Saniyelik hız limiti",
  "hero.chip.sla": "%99,9 erişilebilirlik SLA'sı",
  "hero.base": "Temel URL",
  "hero.auth": "Doğrulama",
  "hero.version": "Sürüm",

  "landing.metrics.title": "Canlı platform metrikleri",
  "landing.metrics.subtitle": "Üretim veritabanımızdan gerçek değerler — şişirilmiş rakam yok.",
  "landing.metrics.developers": "Geliştirici",
  "landing.metrics.products": "Yayında API",
  "landing.metrics.subscriptions": "Aktif abonelik",
  "landing.metrics.requests": "İstek (30g)",
  "landing.metrics.latency": "Ortanca gecikme",
  "landing.metrics.uptime": "Erişilebilirlik (30g)",
  "landing.metrics.status": "Tüm sistemler çalışıyor",
  "landing.metrics.live": "Canlı",

  "landing.products.eyebrow": "API'larımız",
  "landing.products.title": "LoftPress tarafından sunulan API'lar",
  "landing.products.subtitle": "Loft Press tarafından işletilen birinci taraf geliştirici ve veri servisleri. Her API canlı test alanı, dört dilde kod örneği ve öngörülebilir fiyatlarla gelir.",
  "landing.products.explore": "İncele",
  "landing.products.pro_from": "Pro’dan itibaren",
  "landing.products.coming_soon": "Yakında",
  "landing.products.attribution": "API'lar, lisanslı veya açık veri kaynakları kullanılarak LoftPress altyapısı üzerinden sunulur.",

  "landing.pricing.eyebrow": "Fiyatlandırma",
  "landing.pricing.title": "Basit, şeffaf fiyatlandırma",
  "landing.pricing.subtitle": "Ücretsiz başla. Gerektiğinde yükselt. İstediğin zaman iptal et.",
  "landing.pricing.popular": "En popüler",
  "landing.pricing.trial": "Deneme",
  "landing.pricing.pro": "Pro",
  "landing.pricing.enterprise": "Kurumsal",
  "landing.pricing.custom": "Özel",
  "landing.pricing.per_month": "/ay",
  "landing.pricing.reqs": "istek",
  "landing.pricing.rate": "istek/sn",
  "landing.pricing.feat.trial.keys": "1 API anahtarı",
  "landing.pricing.feat.trial.rate": "5 istek/saniye",
  "landing.pricing.feat.trial.support": "Topluluk desteği",
  "landing.pricing.feat.pro.keys": "5 API anahtarı",
  "landing.pricing.feat.pro.rate": "50 istek/saniye",
  "landing.pricing.feat.pro.sla": "%99,9 erişilebilirlik SLA'sı",
  "landing.pricing.feat.pro.support": "E-posta desteği",
  "landing.pricing.feat.ent.keys": "25 API anahtarı",
  "landing.pricing.feat.ent.rate": "250 istek/saniye",
  "landing.pricing.feat.ent.support": "Öncelikli + özel destek",
  "landing.pricing.feat.ent.audit": "SLA, denetim logları, IP izin listesi",
  "pricing.tax.suffix": "+ geçerli vergiler",
  "pricing.tax.note": "KDV, GST veya diğer geçerli satış vergileri müşterinin konumuna göre Creem tarafından ödeme sırasında hesaplanır ve tahsil edilir.",

  "landing.trust.eyebrow": "Güven",
  "landing.trust.title": "Güven & güvenlik",
  "landing.trust.subtitle": "Altyapı ekiplerinin beklediği kontroller — eksiksiz.",
  "landing.trust.hashed.title": "Şifreli anahtarlar",
  "landing.trust.hashed.body": "API anahtarları bcrypt ile hash’lenerek saklanır. Tam sır yalnızca oluşturma anında gösterilir; istediğin zaman yenile veya iptal et.",
  "landing.trust.ipallow.title": "IP izin listesi",
  "landing.trust.ipallow.body": "Panelinden herhangi bir API anahtarını güvenilir IP’lerle sınırla.",
  "landing.trust.audit.title": "Denetim kayıtları",
  "landing.trust.audit.body": "Anahtar oluşturma, yenileme, iptal gibi güvenliğe duyarlı her işlem aktör ve IP ile loglanır.",
  "landing.trust.spend.title": "Harcama limitleri",
  "landing.trust.spend.body": "Hesap başına aylık harcama ve kullanım limitleri belirle; limite yaklaşınca haber al.",

  "landing.dev.eyebrow": "Geliştirici odaklı",
  "landing.dev.title": "İlk günden geliştirici odaklı",
  "landing.dev.subtitle": "Tek API anahtarı, tek temel URL, tek faturalama. Kopyala, yapıştır, yayına al.",
  "landing.dev.one_url": "Tek temel URL",
  "landing.dev.one_auth": "Basit kimlik doğrulama",
  "landing.dev.one_env": "Standart yanıt zarfı",
  "landing.dev.one_url_desc": "loftpress.shop/api/v1/{product}",
  "landing.dev.one_auth_desc": "Standart X-API-Key başlığı. Anahtarları tek tıkla yenile.",
  "landing.dev.one_env_desc": "Her yanıtta success, data, request_id ve credits_remaining alanları bulunur.",

  "landing.faq.eyebrow": "SSS",
  "landing.faq.title": "Sıkça sorulanlar",
  "landing.faq.q1": "Başlamak için kart gerekli mi?",
  "landing.faq.a1": "Hayır. Her hesap Deneme planıyla başlar; ayda 500 istek ve hediye 500 kredi kart bilgisi olmadan verilir.",
  "landing.faq.q2": "Faturalama nasıl işliyor?",
  "landing.faq.a2": "Her ürünün aylık planı vardır (Deneme, Pro, Kurumsal). İstekler plan kotandan sayılır. Krediler, süresi dolmayan opsiyonel yüklemelerdir ve plan kotanın üzerinde istek başı hesaplama için kullanılır.",
  "landing.faq.q3": "Tek hesapla tüm API'ları kullanabilir miyim?",
  "landing.faq.a3": "Evet. LoftPress hesap seviyesinde bir sayaç olarak tasarlandı: tek giriş, tek faturalama yüzeyi ve ürün başına bağımsız kotalı API anahtarları.",
  "landing.faq.q4": "Hangi SLA'ları sunuyorsunuz?",
  "landing.faq.a4": "Pro planlar %99,9 erişilebilirlik SLA'sı içerir. Kurumsal planlar özel SLA, öncelikli destek ve denetim logları içerir.",
  "landing.faq.q5": "Ödemeleri nasıl alıyorsunuz?",
  "landing.faq.a5": "Ödemeler, Merchant of Record olarak Creem tarafından işleniyor. Kart bilgileri sunucularımıza asla dokunmaz; her ödeme, iade ve fatura imzalı webhook'larla Creem üzerinden yönetilir.",
  "landing.faq.q6": "Veriler nereden geliyor?",
  "landing.faq.a6": "Kur verileri, kamuya açık ECB kaynaklarından beslenen LoftPress FX boru hatları üzerinden sunulur. Tatil takvimleri açık veri kümelerinden alınır. IP Zekâsı, ticari lisans yapılandırması tamamlandıktan sonra IPinfo.io ile sunulacaktır. Tüm API'lar LoftPress altyapısı üzerinden çalışır — uç noktaları LoftPress işletir, kullanımı ölçer ve desteği sağlar.",

  "footer.terms": "Şartlar",
  "footer.privacy": "Gizlilik",
  "footer.aup": "Kabul Edilebilir Kullanım",
  "footer.status": "Durum",
  "footer.contact": "İletişim",
  "footer.tagline": "LoftPress API platformu.",
  "footer.product": "Ürün",
  "footer.legal": "Yasal",
  "footer.company": "Şirket",
  "seo.landing.title": "Modern operasyonlar için API'ler",
  "seo.landing.desc": "LoftPress'ten birinci taraf geliştirici API'leri: kur, tatiller, KDV, e-posta, IBAN, QR, telefon, coğrafya, zaman dilimi ve metin — tek gateway, tek fatura.",
  "seo.marketplace.title": "API Pazaryeri",
  "seo.marketplace.desc": "Tüm LoftPress birinci taraf API'lerini kategoriye göre keşfedin — Finans, Yardımcı Araçlar, Güvenlik.",
  "seo.docs.title": "Dokümantasyon",
  "seo.docs.desc": "Her LoftPress API'si için başlangıç, kimlik doğrulama, hız limitleri, hatalar ve kod örnekleri.",
  "seo.status.title": "Platform Durumu",
  "seo.status.desc": "Her LoftPress API'si için canlı çalışma durumu ve 60 günlük uptime geçmişi.",
  "footer.copyright": "Tüm hakları saklıdır.",
  "onboarding.eyebrow": "Başlangıç",
  "onboarding.title": "3 adımda ilk API çağrınızı yapın",
  "onboarding.step1": "API anahtarı oluşturun",
  "onboarding.step1_cta": "API anahtarlarını aç",
  "onboarding.step2": "Örnek isteği kopyalayın (curl)",
  "onboarding.step2_cta": "Örneği kopyala",
  "onboarding.step3": "Playground'dan ilk çağrınızı yapın",
  "onboarding.step3_cta": "Playground'u aç",
  "onboarding.copied": "Kopyalandı — terminale yapıştırın",

  "auth.login.title": "Tekrar hoş geldin",
  "auth.login.welcome": "Tekrar hoş geldin",
  "auth.login.subtitle": "LoftPress hesabına giriş yap.",
  "auth.register.title": "Hesabını oluştur",
  "auth.register.subtitle": "Kart gerekmez — 500 ücretsiz kredi hediye.",
  "auth.email": "E-posta",
  "auth.password": "Şifre",
  "auth.name": "Ad Soyad",
  "auth.submit.login": "Giriş yap",
  "auth.submit.register": "Hesap oluştur",
  "auth.forgot": "Şifreni mi unuttun?",
  "auth.have": "Hesabın var mı?",
  "auth.no": "Hesabın yok mu?",
  "auth.signIn": "Giriş yap",
  "auth.signUp": "Kaydol",
  "auth.forgot.title": "Şifreni sıfırla",
  "auth.forgot.subtitle": "Şifreni sıfırlamak için bir bağlantı göndereceğiz. Gelen kutunu ve spam klasörünü kontrol et.",
  "auth.forgot.submit": "Sıfırlama bağlantısı gönder",
  "auth.forgot.sent_title": "Gelen kutunu kontrol et.",
  "auth.forgot.sent_body": "E-postan sistemde varsa sıfırlama bağlantısı gönderilir.",
  "auth.forgot.back": "Girişe dön",
  "auth.reset.title": "Yeni şifre oluştur",
  "auth.reset.subtitle": "En az 8 karakterli güçlü bir şifre seç.",
  "auth.reset.submit": "Şifreyi sıfırla",
  "auth.reset.missing": "URL’de token eksik.",
  "auth.consent.line": "{links} metinlerini okudum ve kabul ediyorum.",
  "auth.consent.terms": "Kullanım Koşulları",
  "auth.consent.privacy": "Gizlilik Politikası",
  "auth.consent.kvkk": "KVKK Aydınlatma Metni",
  "auth.consent.subscription": "Abonelik Koşulları",
  "auth.consent.marketing": "Ürün güncellemelerini ve kampanyaları almak istiyorum (opsiyonel).",
  "auth.consent.required": "Devam etmek için sözleşmeleri kabul etmelisin.",
  "auth.dev_hint": "Demo bilgiler",

  "dash.overview": "Genel bakış",
  "dash.subs": "Abonelikler",
  "dash.keys": "API anahtarları",
  "dash.usage": "Kullanım",
  "dash.billing": "Faturalama",
  "dash.credits": "Krediler",
  "dash.settings": "Ayarlar",
  "dash.section.account": "Hesap",
  "dash.consents": "Onaylar",

  "dash.overview.title": "Genel bakış",
  "dash.overview.subtitle": "API’larını bir bakışta izlemen için ihtiyacın olan her şey.",
  "dash.overview.stat.credits": "Kullanılabilir kredi",
  "dash.overview.stat.credits_hint": "Kredi bakiyesi",
  "dash.overview.stat.subs": "Aktif abonelik",
  "dash.overview.stat.keys": "API anahtarı",
  "dash.overview.stat.requests": "İstek (30g)",
  "dash.overview.stat.errors": "hata",
  "dash.overview.chart.title": "Günlük kullanım · son 14 gün",
  "dash.overview.chart.axis": "istek / gün",
  "dash.overview.subs.title": "Aktif abonelikler",
  "dash.overview.subs.manage": "Yönet",
  "dash.overview.subs.empty": "Aktif aboneliğin yok.",
  "dash.overview.errors.title": "Hatalar",
  "dash.overview.errors.body": "Ayrıntılı logları görüntüle, ürüne göre filtrele ve tek tek hatalara in.",
  "dash.overview.errors.view": "Kullanım loglarını gör",
  "dash.overview.browse": "API’ları incele",
  "dash.overview.in_30d": "30 günde",

  "dash.subs.title": "Abonelikler",
  "dash.subs.subtitle": "Aktif ve geçmiş ürün aboneliklerini yönet.",
  "dash.subs.empty_title": "Henüz aboneliğin yok.",
  "dash.subs.browse": "API’ları incele",
  "dash.subs.used": "Kullanılan",
  "dash.subs.limit": "Limit",
  "dash.subs.period_end": "Dönem sonu",
  "dash.subs.upgrade": "Planı yükselt / değiştir",
  "dash.subs.cancel": "İptal et",
  "dash.subs.cancel_note": "Erişim dönem sonuna kadar sürer. Hemen kapatmak için Creem müşteri portalını kullanın.",
  "dash.subs.trial_note": "Ücretsiz deneme dönem sonunda otomatik biter.",
  "dash.subs.badge.cancels_at_period_end": "Dönem sonunda iptal",
  "dash.subs.badge.active": "aktif",
  "dash.subs.badge.trialing": "deneme",
  "dash.subs.badge.canceled": "iptal edildi",
  "dash.subs.badge.expired": "süresi doldu",
  "dash.subs.badge.past_due": "gecikmiş",
  "dash.subs.badge.paused": "duraklatıldı",
  "dash.subs.portal": "Faturalamayı yönet (Creem portalı)",
  "dash.subs.portal_missing": "Faturalama portalını açmak için önce ödemeli bir işlem yap.",

  "dash.keys.title": "API anahtarları",
  "dash.keys.subtitle": "Her ürün için ayrı anahtar oluştur. Yenile veya iptal et — tam sır sadece bir kez gösterilir.",
  "dash.keys.create": "Anahtar oluştur",
  "dash.keys.no_sub": "Anahtar oluşturmak için önce aktif bir aboneliğin olmalı.",
  "dash.keys.browse": "API’ları incele",
  "dash.keys.col.name": "Ad",
  "dash.keys.col.product": "Ürün",
  "dash.keys.col.prefix": "Önek",
  "dash.keys.col.last_used": "Son kullanım",
  "dash.keys.col.status": "Durum",
  "dash.keys.col.actions": "İşlemler",
  "dash.keys.empty": "Henüz API anahtarın yok.",
  "dash.keys.status.active": "aktif",
  "dash.keys.status.revoked": "iptal edildi",
  "dash.keys.never": "hiç kullanılmadı",
  "dash.keys.modal.title": "API anahtarı oluştur",
  "dash.keys.modal.product": "Ürün",
  "dash.keys.modal.product_placeholder": "Seç…",
  "dash.keys.modal.name": "Ad",
  "dash.keys.modal.name_placeholder": "Üretim backend",
  "dash.keys.secret.title": "API anahtarını şimdi kopyala",
  "dash.keys.secret.body": "Tam sırrı yalnızca bu ekranda görebileceksin.",
  "dash.keys.secret.copy": "Panoya kopyala",
  "dash.keys.secret.done": "Tamamlandı",
  "dash.keys.rotate": "Yenile",
  "dash.keys.revoke": "İptal",

  "dash.usage.title": "Kullanım & istekler",
  "dash.usage.subtitle": "Hesabındaki en son 100 API isteği.",
  "dash.usage.col.time": "Zaman",
  "dash.usage.col.endpoint": "Uç nokta",
  "dash.usage.col.status": "Durum",
  "dash.usage.col.latency": "Gecikme",
  "dash.usage.col.credits": "Kredi",
  "dash.usage.empty": "Henüz istek yok.",

  "dash.billing.title": "Sipariş geçmişi",
  "dash.billing.subtitle": "Kayıtlarınız için satın alma özetleri. Resmî vergi faturası ve makbuz, Merchant of Record olarak Creem tarafından düzenlenir.",
  "dash.billing.mor_notice": "Resmî vergi faturası ve makbuzu Creem düzenler. LoftPress yalnızca bilgilendirme amaçlı satın alma özeti sağlar.",
  "dash.billing.col.invoice": "Sipariş",
  "dash.billing.col.date": "Tarih",
  "dash.billing.col.product": "Ürün",
  "dash.billing.col.subtotal": "Ara toplam",
  "dash.billing.col.tax": "Vergi",
  "dash.billing.col.total": "Toplam",
  "dash.billing.col.amount": "Tutar",
  "dash.billing.col.status": "Durum",
  "dash.billing.col.provider": "Sağlayıcı",
  "dash.billing.col.action": "İşlem",
  "dash.billing.empty": "Henüz sipariş yok.",
  "dash.billing.download": "Satın alma özeti",
  "dash.billing.download_error": "Satın alma özeti indirilemedi. Lütfen tekrar deneyin.",
  "dash.billing.view": "Görüntüle",
  "dash.billing.external_receipt": "Resmî Creem makbuzu",
  "dash.billing.env.test": "Test",
  "dash.billing.env.live": "Canlı",
  "dash.billing.type.credits": "Kredi",
  "dash.billing.type.subscription": "Abonelik",
  "dash.billing.status.paid": "Ödendi",
  "dash.billing.status.refunded": "İade edildi",
  "dash.billing.status.pending": "Bekliyor",
  "dash.billing.status.failed": "Başarısız",
  "dash.billing.detail.title": "Satın alma özeti",
  "dash.billing.detail.disclaimer": "Bu belge LoftPress tarafından sağlanan bir satın alma özetidir. Vergi faturası değildir — resmî fatura ve makbuzu Merchant of Record olarak Creem düzenler.",
  "dash.billing.detail.qty": "Adet",
  "dash.billing.detail.unit_price": "Birim fiyat",
  "dash.billing.detail.refunded": "İade edildi",
  "dash.billing.detail.net_paid": "Net ödenen",
  "dash.billing.detail.timeline": "İade ve itiraz geçmişi",
  "dash.billing.status.refunded_full": "İade edildi",
  "dash.billing.status.refunded_partial": "Kısmen iade",
  "dash.billing.status.disputed": "İtiraz açık",
  "dash.billing.status.chargeback": "Chargeback",
  "dash.billing.freeze_banner": "Hesabınızdaki açık bir itiraz incelenirken kredi yüklemeleri geçici olarak devre dışı bırakıldı. Sonuçlandığında lütfen destek ekibiyle iletişime geçin.",
  "dash.billing.badge.refunded": "İADE",
  "dash.billing.badge.partial": "KISMİ İADE",
  "dash.billing.badge.disputed": "İTİRAZ",
  "dash.billing.badge.chargeback": "CHARGEBACK",

  "dash.credits.title": "Krediler",
  "dash.credits.subtitle": "Krediler, plan kotanın üzerine ek istekler için kullanılır. Süresi dolmaz.",
  "dash.credits.balance": "Kullanılabilir bakiye",
  "dash.credits.topup.title": "Kredi yükle",
  "dash.credits.topup.subtitle": "Tam sayı USD tutar gir ve Creem üzerinden güvenle öde.",
  "dash.credits.topup.amount_label": "Tutar (USD)",
  "dash.credits.topup.credits_label": "Alacağın kredi",
  "dash.credits.topup.bonus": "bonus",
  "dash.credits.topup.rate": "1 USD = {per} kredi",
  "dash.credits.topup.integer_only": "Sadece tam sayı USD gir (kuruşlu değer kabul edilmez).",
  "dash.credits.topup.range": "{min} ile {max} arasında",
  "dash.credits.topup.button": "Güvenli ödemeye devam et",
  "dash.credits.topup.note": "Ödemeyi tamamlamak için Creem’e yönlendirileceksin. Krediler yalnızca webhook doğrulandıktan sonra eklenir.",
  "dash.credits.txs.title": "Hareketler",
  "dash.credits.txs.col.date": "Tarih",
  "dash.credits.txs.col.reason": "Sebep",
  "dash.credits.txs.col.delta": "Değişim",
  "dash.credits.txs.col.balance": "Bakiye",
  "dash.credits.txs.empty": "Henüz hareket yok.",

  "dash.settings.title": "Ayarlar",
  "dash.settings.subtitle": "Webhook, IP izin listesi, harcama limitleri, dil.",
  "dash.settings.webhook": "Webhook URL",
  "dash.settings.webhook_placeholder": "https://uygulaman.com/loftpress/webhook",
  "dash.settings.webhook_hint": "Kullanım ve faturalama olaylarını imzalı bir gövde ile buraya POST ederiz.",
  "dash.settings.allowlist": "IP izin listesi",
  "dash.settings.allowlist_placeholder": "Her satıra bir IP — boş bırakırsan tümüne izin verilir",
  "dash.settings.spend_limit": "Aylık harcama limiti (USD)",
  "dash.settings.language": "Dil",
  "dash.settings.lang_en": "English",
  "dash.settings.lang_tr": "Türkçe",
  "dash.settings.alerts": "Aylık kotanın %80’ine ulaşınca e-posta gönder",
  "dash.settings.save": "Değişiklikleri kaydet",

  "marketplace.title": "LoftPress tarafından sunulan API'lar",
  "marketplace.subtitle": "Loft Press tarafından işletilen birinci taraf geliştirici ve veri servisleri. Her API öngörülebilir fiyat, canlı test alanı ve dört dilde kod örneğiyle gelir.",
  "marketplace.search": "API, kategori veya kullanım senaryosu ara…",
  "marketplace.all": "Tümü",
  "marketplace.card.trial": "Deneme",
  "marketplace.card.pro": "Pro",
  "marketplace.card.ent": "Kurumsal",
  "marketplace.pending_config": "Yapılandırma bekliyor",
  "marketplace.coming_soon": "Yakında",
  "marketplace.empty": "Aramanla eşleşen API yok.",

  "api.usecases": "Kullanım senaryoları",
  "api.endpoints": "Uç noktalar",
  "api.base": "Temel",
  "api.code_examples": "Kod örnekleri",
  "api.sample_response": "Örnek yanıt",
  "api.playground.title": "Canlı test alanı",
  "api.playground.run": "İsteği çalıştır",
  "api.playground.running": "Çalıştırılıyor…",
  "api.playground.note": "API anahtarı gerekmez. IP başına 30 istek/dakika ile sınırlıdır.",
  "api.playground.placeholder": "// API’yi denemek için Çalıştır’a bas",
  "api.plans.title": "Plan fiyatlandırması",
  "api.plans.subscribe": "Abone ol",
  "api.plans.contact": "Satışa ulaş",
  "api.plans.pending": "Henüz satın alınamıyor",
  "api.plans.pending_body": "Bu API şu anda ticari kullanım için hazırlanıyor. Kısa süre sonra tekrar kontrol et.",
  "api.status.operational": "çalışıyor",
  "api.status.maintenance": "bakım",
  "api.status.pending": "yapılandırma bekliyor",
  "api.status.all_ok": "Tüm sistemler çalışıyor.",
  "api.status.live": "Canlı durum",
  "api.col.method": "Metod",
  "api.col.path": "Yol",
  "api.col.description": "Açıklama",
  "api.col.credits": "Kredi",
  "api.breadcrumb.marketplace": "API'lar",
  "api.detail.back": "API'lara dön",
  "api.detail.maintenance": "bakımda",
  "api.detail.operational": "çalışıyor",
  "api.detail.coming_soon_title": "Yakında",
  "api.detail.coming_soon_body": "Bu API ticari kullanıma yönelik son hazırlıklarını tamamlıyor. Abonelik, API anahtarı verilmesi ve faturalama, lansmana kadar kapalıdır.",
  "api.detail.coming_soon_toast": "Bu API yakında sunulacak; şu an satın alınamıyor.",
  "api.detail.playground_coming_soon": "Bu API yakında sunulacak — canlı playground lansmana kadar devre dışıdır.",
  "api.detail.playground.title": "Canlı playground",
  "api.detail.playground.run": "İsteği çalıştır",
  "api.detail.playground.running": "Çalışıyor…",
  "api.detail.playground.hint": "API anahtarı gerekmez. IP başına dakikada 30 istekle sınırlıdır.",
  "api.detail.playground.click_run": "API'yi denemek için Çalıştır'a tıklayın",
  "api.detail.plans": "Fiyatlandırma planları",
  "api.detail.use_cases": "Kullanım senaryoları",
  "api.detail.endpoints": "Uç noktalar",
  "api.detail.base": "Taban",
  "api.detail.method": "Metot",
  "api.detail.path": "Yol",
  "api.detail.description": "Açıklama",
  "api.detail.credits": "Kredi",
  "api.detail.code_examples": "Kod örnekleri",
  "api.detail.sample_response": "Örnek yanıt",
  "api.detail.changelog_status": "Değişiklikler / Durum",
  "api.detail.live_status": "Canlı durum →",
  "docs.eyebrow": "Dokümantasyon",
  "docs.guides": "Rehberler",
  "docs.products": "Ürünler",
  "docs.getting_started.title": "Başlangıç",
  "docs.getting_started.intro": "LoftPress tek hesaplı bir API platformudur. LoftPress tarafından işletilen her birinci taraf API kendi uç noktalarını /api/v1/{product} altında sunar ve dashboard'unuzdan verilen ürüne özel bir API anahtarı ile kimlik doğrulanır.",
  "docs.getting_started.step1": "Kaydolun ve LoftPress kataloğundan bir API'ye abone olun.",
  "docs.getting_started.step2_pre": "Şu adresten bir API anahtarı oluşturun:",
  "docs.getting_started.step2_link": "Dashboard → API anahtarları",
  "docs.getting_started.step3": "Bir kez gösterilen gizli anahtarı kopyalayın; bcrypt hash olarak saklanır ve sonradan kurtarılamaz.",
  "docs.getting_started.step4": "Gizli anahtarı X-API-Key başlığı olarak gönderin.",
  "docs.auth.title": "Kimlik doğrulama",
  "docs.auth.body": "/api/v1/* altındaki tüm istekler bir API anahtarı gerektirir. Anahtarı X-API-Key başlığı ile geçin. Anahtarlar ürüne özeldir; yanlış ürünle kullanılan bir anahtar 403 wrong_product döndürür.",
  "docs.auth.without_key": "Anahtarsız istek",
  "docs.rate.title": "Hız limitleri",
  "docs.rate.body": "Hız limitleri saniyelik kayan pencere ile API anahtarı bazında uygulanır. Limitler planınıza göre belirlenir:",
  "docs.rate.trial": "Deneme",
  "docs.rate.trial_desc": "5 istek/saniye · 500 istek/ay · 1 anahtar.",
  "docs.rate.pro": "Pro",
  "docs.rate.pro_desc": "50 istek/saniye · 100.000 istek/ay · 5 anahtar.",
  "docs.rate.ent": "Kurumsal",
  "docs.rate.ent_desc": "özel kapasite ve anahtar sayısı.",
  "docs.rate.exceed": "Saniyelik limiti aşan istekler 429 rate_limited döndürür.",
  "docs.errors.title": "Hata kodları",
  "docs.errors.code": "Kod",
  "docs.errors.meaning": "Anlamı",
  "docs.errors.401_missing": "X-API-Key başlığı sağlanmadı",
  "docs.errors.401_invalid": "Anahtar iptal edildi veya bilinmiyor",
  "docs.errors.402_quota": "Aylık plan kotasına ulaşıldı",
  "docs.errors.402_sub": "Abonelik iptal edildi veya vadesi geçti",
  "docs.errors.403_wrong": "Anahtar farklı bir ürün için verilmiş",
  "docs.errors.403_ip": "İstemci IP'si izin listesinde değil",
  "docs.errors.429": "Saniyelik hız limiti aşıldı",
  "docs.errors.503": "Servis planlı bakımda",
  "docs.webhooks.title": "Webhook'lar",
  "docs.webhooks.body1_pre": "Webhook URL'sini şu adresten yapılandırın:",
  "docs.webhooks.body1_link": "Dashboard → Ayarlar",
  "docs.webhooks.body1_post": "LoftPress kullanım ve fatura olaylarını imzalı X-LoftPress-Signature başlığı ile uç noktanıza POST eder.",
  "docs.webhooks.body2": "Ödeme webhook'ları /api/billing/webhook/{provider} adresinde sunulur. İmza doğrulaması sağlayıcıya özeldir ve production'a alınmadan önce etkinleştirilmelidir.",
  "docs.sdk.title": "Kod örnekleri",
  "status.eyebrow": "Durum",
  "status.title": "Platform durumu",
  "status.all_operational": "Tüm sistemler çalışıyor",
  "status.some_degraded": "Bazı servisler kısıtlı",
  "status.services": "Servisler",
  "status.uptime_60d": "60 günlük çalışma süresi",
  "status.60d_ago": "60 gün önce",
  "status.today": "bugün",
  "status.recent_incidents": "Son olaylar",
  "status.no_incidents": "Kayıtlı olay yok.",
  "status.chip.operational": "çalışıyor",
  "status.chip.maintenance": "bakımda",
  "status.chip.down": "kapalı",
  "status.chip.degraded": "kısıtlı",
  "api.detail.not_found": "Ürün bulunamadı",
  "api.detail.custom_price": "Özel",
  "api.detail.pending_title": "Şu an satın alınamıyor",
  "api.detail.pending_body": "Bu API ticari kullanıma yönelik yapılandırılıyor. Lütfen kısa süre içinde tekrar kontrol edin.",
  "api.detail.pending_toast": "Bu API şu an satın alınamıyor.",
  "api.detail.contact_sales": "Satış ekibiyle görüş",

  "checkout.redirecting": "Güvenli ödeme sayfasına yönlendiriliyorsun…",

  "payment.success.title": "Ödeme başarılı",
  "payment.success.body": "Alışverişin için teşekkürler. Ödeme doğrulandıktan sonra erişimin birkaç saniye içinde açılır.",
  "payment.success.cta": "Panele git",
  "payment.cancel.title": "Ödeme iptal edildi",
  "payment.cancel.body": "Ücretlendirme yapılmadı. Kredi veya plan sayfasından tekrar deneyebilirsin.",
  "payment.cancel.cta": "Panele dön",
  "payment.pending.title": "Ödemen işleniyor",
  "payment.pending.body": "Ödemeni doğruluyoruz. Bu işlem bir dakikaya kadar sürebilir.",
  "payment.pending.cta": "Paneli yenile",

  "cookie.title": "Zorunlu çerezler kullanıyoruz",
  "cookie.body": "Kimlik doğrulama için zorunlu çerezler ve onayınla birlikte ürünü iyileştirmek için opsiyonel analitik çerezleri kullanıyoruz.",
  "cookie.accept_all": "Tümünü kabul et",
  "cookie.only_necessary": "Sadece gerekli",
  "cookie.manage": "Tercihleri yönet",

  "docs.eyebrow": "Dokümantasyon",
  "docs.guides": "Rehberler",
  "docs.products": "Ürünler",
  "docs.getting_started": "Başlarken",
  "docs.auth": "Kimlik doğrulama",
  "docs.rate_limits": "Hız limitleri",
  "docs.errors": "Hata kodları",
  "docs.webhooks": "Webhook’lar",
  "docs.sdk": "Kod örnekleri",

  "status.eyebrow": "Durum",
  "status.title": "Platform durumu",
  "status.all_ok": "Tüm sistemler çalışıyor",
  "status.degraded": "Bazı servisler kısıtlı",
  "status.services": "Servisler",
  "status.history_title": "60 günlük erişilebilirlik",
  "status.days_ago": "60 gün önce",
  "status.today": "bugün",
  "status.incidents_title": "Son olaylar",
  "status.incidents_empty": "Kayıtlı olay yok.",

  "legal.eyebrow": "Yasal",
  "legal.pending": "Bu belge henüz yayımlanmadı. Kısa süre sonra tekrar kontrol et.",
  "legal.home": "Ana sayfa",
  "legal.version": "Sürüm",
  "legal.updated": "Son güncelleme",
  "legal.print": "Yazdır",
  "legal.toc": "Bu sayfada",
  "legal.support": "Destek",
  "contact.form.title": "Bize mesaj gönderin",
  "contact.form.name": "Ad Soyad",
  "contact.form.email": "E-posta adresi",
  "contact.form.category": "Konu",
  "contact.form.order": "Sipariş ID (opsiyonel)",
  "contact.form.order_ph": "örn. ord_abc123",
  "contact.form.message": "Mesajınız",
  "contact.form.submit": "Gönder",
  "contact.form.ack.pre": "Okuyup anladım:",
  "contact.form.ack.privacy": "Gizlilik Politikası",
  "contact.form.note": "3 iş günü içinde yanıtlıyoruz (Pzt–Cum, 09:00–17:00 UK). Lütfen parola veya API anahtarı yapıştırmayın.",
  "contact.form.success": "Mesajınız iletildi. Kısa sürede size döneceğiz.",
  "contact.form.error": "Mesajınız gönderilemedi. Lütfen birazdan tekrar deneyin.",
  "contact.form.thanks": "Teşekkürler — mesajınız ekibimize iletildi. 3 iş günü içinde yanıtlıyoruz.",
  "contact.cat.sales": "Satış ve fiyatlandırma",
  "contact.cat.technical": "Teknik destek",
  "contact.cat.billing": "Faturalama / iade",
  "contact.cat.privacy": "Gizlilik / veri talebi",
  "contact.cat.legal": "Hukuki bildirim",
  "contact.cat.security": "Güvenlik bildirimi",
  "contact.cat.other": "Diğer",
  "checkout.mor.notice": "Ödeme, Merchant of Record olarak Creem tarafından işlenir.",
  "checkout.mor.link": "Creem Alıcı Şartları ve Gizlilik",
  "checkout.mor.notice_short": "Ödeme: Creem MoR",
  "checkout.consent.required": "Lütfen önce Kullanım Koşulları ve Gizlilik Politikası'nı kabul edin.",
  "checkout.consent.terms.pre": "Okudum ve kabul ediyorum:",
  "checkout.consent.terms.link": "Kullanım Koşulları",
  "checkout.consent.and": "ve",
  "checkout.consent.privacy.link": "Gizlilik Politikası",
  "api.detail.confirm.body": "Aboneliği onaylayın. Ödeme, Merchant of Record olarak Creem tarafından işlenir.",
  "api.detail.confirm.cta": "Creem'e devam et",
  "common.cancel": "Vazgeç",
  "footer.cookie_prefs": "Çerez tercihleri",
  "footer.refund": "İade ve iptal",

  "admin.section.admin": "Yönetim",
  "admin.overview": "Genel bakış",
  "admin.users": "Kullanıcılar",
  "admin.products": "Ürünler",
  "admin.plans": "Planlar",
  "admin.credit_packs": "Kredi paketleri",
  "admin.incidents": "Olaylar",
  "admin.settings": "Ayarlar",
  "admin.creem": "Creem ayarları",
  "admin.creem_products": "Creem ürün senkronizasyonu",
  "admin.resources": "API kaynakları",
  "admin.company": "Şirket bilgileri",
  "admin.legal": "Hukuki metinler",
  "admin.consents": "Onay kayıtları",
  "admin.refunds": "İadeler & itirazlar",
  "admin.webhooks": "Webhook olayları",
  "admin.cases": "Vakalar",
  "admin.audit": "Denetim log",
  "admin.reconciliation": "Mutabakat",
  "admin.analytics": "Analitik",
  "admin.products_v2": "Ürün Yönetimi",
  "admin.plans_v2": "Plan Yönetimi",
  "admin.bulk": "Toplu işlemler",
  "adminp.products.title": "Ürün Yönetimi",
  "adminp.products.subtitle": "Kurumsal CRUD, değiştirilemez sürüm geçmişiyle yayın/rollback, yalnızca bir kez gösterilen secret rotasyonu.",
  "adminp.products.new": "Yeni ürün",
  "adminp.products.search_ph": "Slug, ad veya açıklamaya göre ara…",
  "adminp.products.all_statuses": "Tüm durumlar",
  "adminp.products.total": "ürün",
  "adminp.products.empty": "Eşleşen ürün yok.",
  "adminp.products.col.name": "Ad",
  "adminp.products.col.slug": "Slug",
  "adminp.products.col.status": "Durum",
  "adminp.products.col.version": "Sürüm",
  "adminp.products.col.updated": "Son güncelleme",
  "adminp.products.publish": "Yayınla",
  "adminp.products.archive": "Arşivle",
  "adminp.products.reactivate": "Tekrar aktifleştir",
  "adminp.products.rotate_secret": "Secret döndür",
  "adminp.products.rotate": "Döndür",
  "adminp.products.rotate_warning": "Yeni secret yalnızca bir kez gösterilir. Şimdi kopyalayın — bir daha görüntülenmeyecektir.",
  "adminp.products.rotate_reveal_prompt": "Bu secret'i kopyalayın — görüntülenebileceği TEK an bu:",
  "adminp.products.rotate_copied": "Secret panoya kopyalandı",
  "adminp.products.versions": "Sürüm geçmişi",
  "adminp.products.secrets": "Secret'lar",
  "adminp.products.no_secrets": "Henüz secret yok.",
  "adminp.products.created": "Ürün taslağı oluşturuldu",
  "adminp.plans.title": "Plan Yönetimi",
  "adminp.plans.subtitle": "Değiştirilemez plan sürümleme. Mevcut aboneler snapshot'larında kalır; yeni aboneler geçerli sürümü alır.",
  "adminp.plans.new": "Yeni plan",
  "adminp.plans.tier_name_required": "Tier ve görünen ad zorunludur.",
  "adminp.plans.created": "Plan lineage oluşturuldu (taslak v1).",
  "adminp.plans.empty": "Henüz plan yok.",
  "adminp.plans.total": "plan",
  "adminp.plans.col.tier": "Tier",
  "adminp.plans.col.name": "Görünen ad",
  "adminp.plans.col.status": "Durum",
  "adminp.plans.col.version": "Geçerli sürüm",
  "adminp.plans.col.price": "Fiyat",
  "adminp.plans.col.effective": "Etkin tarih",
  "adminp.plans.versions": "Sürümler",
  "adminp.plans.draft_version": "Yeni taslak sürüm",
  "adminp.plans.draft_snapshot_prompt": "Yeni taslak sürüm için JSON snapshot yapıştırın",
  "adminp.plans.draft_reason_prompt": "Sebep (denetim logu, min 3 karakter)",
  "adminp.plans.publish_reason_prompt": "Bu sürümü yayınlama sebebi (denetim logu)",
  "adminp.plans.rollback_reason_prompt": "Rollback sebebi (denetim logu). Bu, önceki satırları değiştirmeden yeni bir sürüm oluşturur.",
  "adminp.analytics.title": "Analitik",
  "adminp.analytics.subtitle": "Veritabanından gerçek rakamlar. MRR tek seferlik kredi alımlarını içermez; iade/itiraz tutarları net gelirden düşer.",
  "adminp.analytics.gross": "Brüt",
  "adminp.analytics.refunded": "İade",
  "adminp.analytics.disputed": "İtirazlı",
  "adminp.analytics.net": "Net",
  "adminp.analytics.mrr": "MRR",
  "adminp.analytics.arr": "ARR",
  "adminp.analytics.arpu": "ARPU",
  "adminp.analytics.churn": "Churn",
  "adminp.analytics.credits_sold": "Satılan kredi",
  "adminp.analytics.credits_spent": "Harcanan kredi",
  "adminp.analytics.liability": "Kredi yükümlülüğü",
  "adminp.analytics.api_success": "API başarı",
  "adminp.analytics.trend": "Günlük gelir trendi",
  "adminp.analytics.top_products": "En çok ürün",
  "adminp.analytics.top_customers": "En çok müşteri",
  "adminp.analytics.top_plans": "En çok plan",
  "adminp.bulk.title": "Toplu işlemler",
  "adminp.bulk.subtitle": "Zorunlu dry-run + doğrulama raporlu CSV içe aktarım. Kredi değişiklikleri değiştirilemez ledger'dan geçer; idempotency çift uygulamayı engeller.",
  "adminp.bulk.template": "Şablon indir",
  "adminp.bulk.dry_run": "Dry-run",
  "adminp.bulk.execute": "Toplu işlemi çalıştır",
  "adminp.bulk.batches": "Son toplu işlemler",
  "adminp.bulk.reason_required": "En az 3 karakterlik sebep zorunludur.",
  "adminp4.title": "Güvenlik & Operasyon",
  "adminp4.subtitle": "2FA, Güvenlik Merkezi, Bildirimler, Sistem Ayarları, Sağlık, Olaylar — hepsi gerçek veri.",
  "adminp4.tab.twofa": "2FA",
  "adminp4.tab.security": "Güvenlik",
  "adminp4.tab.notifications": "Bildirimler",
  "adminp4.tab.settings": "Ayarlar",
  "adminp4.tab.health": "Sağlık",
  "adminp4.tab.incidents": "Olaylar",
  "adminp4.twofa.title": "İki faktörlü doğrulama",
  "adminp4.twofa.enabled": "2FA etkinleştirildi — kurtarma kodlarını kaydedin",
  "admin.section.overview": "Genel Bakış",
  "admin.section.customers": "Müşteriler",
  "admin.section.commerce": "Ticaret",
  "admin.section.operations": "Operasyon",
  "admin.section.security": "Güvenlik",
  "admin.section.config": "Yapılandırma",
  "admin.products_legacy": "Ürünler (eski)",
  "admin.plans_legacy": "Planlar (eski)",
  "admin.cmdk.placeholder": "Her şeyi ara — kullanıcı, sipariş, iade…",
  "admin.cmdk.hint": "≥ 2 karakter yaz. Enter aç · ↑↓ gez · Esc kapat.",
  "admin.cmdk.no_results": "Eşleşme yok.",
  "admin.recon.title": "Finansal mutabakat",
  "admin.recon.subtitle": "Sadece okuma teşhis. Hiçbir mutation yok. Düzeltmeler Super Admin + dry-run gerekir.",
  "admin.recon.drift": "Ledger sapması olan kullanıcı",
  "admin.recon.orphan": "Öksüz fatura",
  "admin.recon.violations": "İade cap ihlali",
  "admin.recon.stale_freezes": "Eski donma",
  "admin.recon.failed_hooks": "Başarısız webhook",
  "admin.recon.overall": "Genel",
  "admin.recon.drift_users": "Ledger vs bakiye sapması olan kullanıcılar",
  "admin.recon.orphan_list": "Ledger karşılığı olmayan kredi faturaları",
  "admin.recon.violations_list": "Geri alınan kredi verilenden fazla olan faturalar",
  "admin.sessions": "Admin oturumları",
  "admin.sessions.title": "Admin oturum yöneticisi",
  "admin.sessions.subtitle": "Aktif tüm admin oturumları — sunucu tarafı revoke ve audit ile.",
  "admin.sessions.search_ph": "E-posta, IP, tarayıcı ile ara…",
  "admin.sessions.active_only": "Sadece aktif",
  "admin.sessions.suspicious_only": "Sadece şüpheli",
  "admin.sessions.total": "oturum",
  "admin.sessions.empty": "Filtrelere uyan oturum yok.",
  "admin.sessions.this_device": "Bu cihaz",
  "admin.sessions.col.admin": "Yönetici",
  "admin.sessions.col.role": "Rol",
  "admin.sessions.col.device": "Cihaz",
  "admin.sessions.col.login": "Giriş",
  "admin.sessions.col.activity": "Son aktivite",
  "admin.sessions.col.expires": "Süre sonu",
  "admin.sessions.col.status": "Durum",
  "admin.sessions.col.actions": "İşlemler",
  "admin.sessions.revoke": "İptal et",
  "admin.sessions.revoke_title": "Bu oturumu iptal et",
  "admin.sessions.self_confirm": "Bu işlemin mevcut cihazımdan çıkış yapacağını anlıyorum.",
  "admin.sessions.revoked": "Oturum iptal edildi",
  "admin.sessions.revoked_plural": "oturum iptal edildi",
  "admin.sessions.revoke_others": "Diğer oturumlarımı kapat",
  "admin.sessions.revoke_others_title": "Diğer tüm oturumları kapat",
  "admin.sessions.revoke_others_desc": "Bu cihaz dışındaki tüm hesap oturumları sonlandırılacak.",
  "admin.sessions.force_logout": "Admini zorla çıkart",
  "admin.sessions.force_logout_title": "Bu adminin tüm oturumlarını zorla kapat",
  "admin.sessions.force_logout_done": "Admin oturumları sonlandırıldı",
  "admin.sessions.flag": "Şüpheli işaretle",
  "admin.sessions.flag_title": "Bu oturumu şüpheli işaretle",
  "admin.sessions.flagged": "Oturum işaretlendi",
  "admin.recon.run_now": "Yeni tarama başlat",
  "admin.recon.findings_open": "Açık bulgular",
  "admin.recon.findings_critical": "Kritik bulgular",
  "admin.recon.requests_pending": "Onay bekleyen",
  "admin.recon.requests_executed": "Uygulandı",
  "admin.recon.last_run": "Son tarama",
  "admin.recon.tab.overview": "Genel bakış",
  "admin.recon.tab.findings": "Bulgular",
  "admin.recon.tab.approvals": "Onay bekleyenler",
  "admin.recon.search_ph": "E-posta ile ara…",
  "admin.recon.filter.all_severity": "Tüm önem seviyeleri",
  "admin.recon.filter.open": "Sadece açık",
  "admin.recon.filter.resolved": "Sadece çözülmüş",
  "admin.recon.filter.all": "Tümü",
  "admin.recon.findings_total": "bulgu",
  "admin.recon.empty_findings": "Filtrelere uyan bulgu yok.",
  "admin.recon.col.user": "Kullanıcı",
  "admin.recon.col.stored": "Görünen bakiye",
  "admin.recon.col.ledger": "Defter bakiyesi",
  "admin.recon.col.drift": "Fark",
  "admin.recon.col.severity": "Önem",
  "admin.recon.col.source": "Şüpheli kaynak",
  "admin.recon.col.status": "Durum",
  "admin.recon.col.detected": "İlk tespit",
  "admin.recon.dry_run": "Deneme çalıştırması",
  "admin.recon.request_resolution": "Düzeltme talebi oluştur",
  "admin.recon.resolution_request_title": "Düzeltme talebi oluştur",
  "admin.recon.proposed": "Önerilen",
  "admin.recon.request_action": "Talebi gönder",
  "admin.recon.request_created": "Düzeltme talebi oluşturuldu — ilk onay bekleniyor",
  "admin.recon.finding_for": "Bulgu",
  "admin.recon.active_request": "Aktif düzeltme talebi",
  "admin.recon.investigations": "İncelemeler",
  "admin.recon.approve_title": "Düzeltme talebini onayla",
  "admin.recon.approve": "Onayla",
  "admin.recon.approved": "Onay kaydedildi",
  "admin.recon.reject_title": "Düzeltme talebini reddet",
  "admin.recon.reject": "Reddet",
  "admin.recon.rejected": "Reddedildi",
  "admin.recon.execute_title": "Telafi kaydını uygula",
  "admin.recon.execute": "Uygula",
  "admin.recon.executed": "Telafi ledger kaydı oluşturuldu",
  "admin.recon.execute_ack": "Bu işlemin yeni bir immutable telafi ledger kaydı ekleyeceğini ve geri alınamayacağını anlıyorum.",
  "admin.recon.no_pending": "Onay bekleyen düzeltme talebi yok.",
  "admin.dashboard.title": "Yönetici paneli",
  "admin.dashboard.range": "Aralık",
  "admin.dashboard.days_abbrev": "g",
  "admin.dashboard.revenue_trend": "Gelir trendi",
  "admin.kpi.gross": "Brüt gelir",
  "admin.kpi.refunded": "İade edilen",
  "admin.kpi.net": "Net gelir",
  "admin.kpi.active_subs": "Aktif abonelik",
  "admin.kpi.users_total": "Kullanıcı",
  "admin.kpi.new": "yeni",
  "admin.kpi.paying": "Ödeme yapan",
  "admin.kpi.credits_sold": "Satılan kredi",
  "admin.kpi.spent": "tüketildi",
  "admin.kpi.credits_outstanding": "Kredi yükümlülüğü",
  "admin.kpi.open_disputes": "Açık itiraz",
  "admin.kpi.open_reviews": "Manuel inceleme",
  "admin.kpi.failed_webhooks": "Başarısız webhook",
  "admin.kpi.frozen_users": "Dondurulmuş hesap",
  "admin.action.reviews": "Bekleyen manuel incelemeler",
  "admin.action.disputes": "Açık itirazlar",
  "admin.action.failed_webhooks": "Başarısız webhooklar",
  "admin.action.frozen": "Top-up dondurulmuş hesaplar",
  "admin.action.empty": "Her şey yolunda.",
  "admin.cases.title": "İade, itiraz ve manuel inceleme merkezi",
  "admin.cases.subtitle": "Yetersiz bakiye durumunda kalan iadeleri onaylayın, itirazları çözün, hesap dondurup açın.",
  "admin.cases.tab.open": "Açık",
  "admin.cases.tab.resolved": "Çözülmüş",
  "admin.cases.tab.all": "Tümü",
  "admin.cases.col.at": "Açılış",
  "admin.cases.col.kind": "Tür",
  "admin.cases.col.user": "Kullanıcı",
  "admin.cases.col.invoice": "Sipariş",
  "admin.cases.col.amount": "Tutar",
  "admin.cases.col.needed_credits": "Geri alınacak kredi",
  "admin.cases.col.status": "Durum",
  "admin.cases.empty": "Henüz vaka yok.",
  "admin.cases.approve": "Onayla",
  "admin.cases.dismiss": "Reddet",
  "admin.cases.resolve": "Çöz",
  "admin.cases.approve_title": "İade geri alımını onayla",
  "admin.cases.approve_desc": "Immutable ledger üzerinden kredi atomik biçimde düşülecek.",
  "admin.cases.dismiss_title": "Vakayı reddet",
  "admin.cases.dismiss_desc": "Vaka kredi geri alımı yapılmadan kapatılır.",
  "admin.cases.resolve_title": "İtirazı çöz",
  "admin.cases.resolve_desc": "Sonucu seçin. WON durumunda başka açık itiraz yoksa top-up dondurma kaldırılır.",
  "admin.cases.reason": "Sebep (denetim log için zorunlu)",
  "admin.cases.cancel": "İptal",
  "admin.cases.confirm_approve": "Geri alımı onayla",
  "admin.cases.confirm_dismiss": "Reddet",
  "admin.cases.won": "Kazanıldı",
  "admin.cases.lost": "Kaybedildi",
  "admin.cases.approved": "Geri alım uygulandı",
  "admin.cases.dismissed": "Vaka reddedildi",
  "admin.cases.dispute_resolved": "İtiraz çözüldü",
  "admin.webhooks.filter.all": "Tümü",
  "admin.webhooks.filter.failed": "Sadece başarısız",
  "admin.webhooks.filter.processed": "Sadece işlenmiş",
  "admin.webhooks.filter.type": "Event türü…",
  "admin.webhooks.detail": "Webhook olayı",
  "admin.webhooks.confirm_retry": "Bu olayı işlenmemiş olarak işaretle, Creem güvenle yeniden gönderebilir? (İmza değişmez; idempotency duplikasyonu emer.)",
  "admin.webhooks.retry_requested": "Yeniden deneme istendi",
  "admin.webhooks.redaction_notice": "Hassas alanlar görüntülemeden önce her zaman maskeleniyor.",
  "admin.audit.title": "Denetim log",
  "admin.audit.subtitle": "Tüm admin mutation işlemlerinin değiştirilemez kaydı.",
  "admin.audit.filter_action": "Action prefix ile filtrele (örn: refund.)…",
  "admin.audit.col.at": "Zaman",
  "admin.audit.col.actor": "Kim",
  "admin.audit.col.action": "İşlem",
  "admin.audit.col.target": "Hedef",
  "admin.users.title": "Kullanıcılar",
  "admin.users.subtitle": "Ara, filtrele, incele ve her hesap üzerinde işlem yap.",
  "admin.users.search_ph": "E-posta, ad veya kullanıcı ID ile ara…",
  "admin.users.csv": "CSV indir",
  "admin.users.total": "sonuç",
  "admin.users.empty": "Eşleşen kullanıcı yok.",
  "admin.users.suspended": "ASKIDA",
  "admin.users.frozen": "DONDURULMUŞ",
  "admin.users.filter.all": "Tümü",
  "admin.users.filter.paying": "Ödeme yapan",
  "admin.users.filter.frozen": "Dondurulmuş",
  "admin.users.filter.suspended": "Askıya alınmış",
  "admin.users.filter.admin": "Adminler",
  "admin.users.col.user": "Kullanıcı",
  "admin.users.col.credits": "Kredi",
  "admin.users.col.lifetime": "Yaşam boyu",
  "admin.users.col.orders": "Sipariş",
  "admin.users.col.status": "Durum",
  "admin.users.col.risk": "Risk",
  "admin.users.col.joined": "Kayıt",
  "admin.customer.back": "Kullanıcılara dön",
  "admin.customer.credit_adjust": "Kredi düzelt",
  "admin.customer.freeze": "Top-up'ı dondur",
  "admin.customer.unfreeze": "Top-up'ı aç",
  "admin.customer.suspend": "Hesabı askıya al",
  "admin.customer.unsuspend": "Askıyı kaldır",
  "admin.customer.k.credits": "Kredi",
  "admin.customer.k.gross": "Yaşam boyu brüt",
  "admin.customer.k.refunded": "İade",
  "admin.customer.k.net": "Net",
  "admin.customer.k.orders": "Sipariş",
  "admin.customer.k.disputes": "Açık itiraz",
  "admin.customer.tab.overview": "Genel",
  "admin.customer.tab.credits": "Krediler",
  "admin.customer.tab.orders": "Siparişler",
  "admin.customer.tab.subscriptions": "Abonelikler",
  "admin.customer.tab.api_keys": "API anahtarları",
  "admin.customer.tab.refunds": "İadeler",
  "admin.customer.tab.disputes": "İtirazlar",
  "admin.customer.tab.notes": "Notlar",
  "admin.customer.tab.audit": "Denetim",
  "admin.customer.overview.joined": "Kayıt tarihi",
  "admin.customer.overview.language": "Dil",
  "admin.customer.overview.freeze_reason": "Dondurma sebebi",
  "admin.customer.credits_desc": "Mevcut bakiye:",
  "admin.customer.notes.add": "Not ekle",
  "admin.customer.notes.body": "Not",
  "admin.customer.notes.added": "Not eklendi",
  "admin.customer.freeze_done": "Top-up donduruldu",
  "admin.customer.unfreeze_done": "Top-up açıldı",
  "admin.customer.suspend_done": "Hesap askıya alındı",
  "admin.customer.unsuspend_done": "Askı kaldırıldı",
  "admin.customer.delta": "Delta (artı = ekle, eksi = düş)",
  "admin.customer.category": "Kategori",
  "admin.customer.note": "Not (opsiyonel)",
  "admin.customer.reason": "Sebep (zorunlu, denetim log)",
  "admin.customer.cancel": "İptal",
  "admin.customer.confirm": "Uygula",
  "admin.customer.dialog.credit": "Kredi bakiyesini düzelt",
  "admin.customer.dialog.freeze": "Top-up'ı dondur",
  "admin.customer.dialog.unfreeze": "Top-up'ı aç",
  "admin.customer.dialog.suspend": "Hesabı askıya al",
  "admin.customer.dialog.unsuspend": "Askıyı kaldır",
  "admin.customer.dialog.note": "İç not ekle",

  "admin.overview.title": "Yönetim özeti",
  "admin.overview.subtitle": "Platform sağlığı, gelir, kullanım — bir bakışta.",
  "admin.stat.users": "Kullanıcı",
  "admin.stat.products": "Ürün",
  "admin.stat.subs": "Aktif abonelik",
  "admin.stat.requests": "İstek (30g)",
  "admin.stat.errors": "hata",
  "admin.stat.revenue30": "Gelir (30g)",
  "admin.stat.revenue_total": "Toplam gelir",

  "admin.users.title": "Kullanıcılar",
  "admin.users.search": "E-posta veya ad ara…",
  "admin.users.col.name": "Ad",
  "admin.users.col.email": "E-posta",
  "admin.users.col.role": "Rol",
  "admin.users.col.credits": "Kredi",
  "admin.users.col.joined": "Katıldı",
  "admin.users.col.actions": "İşlemler",
  "admin.users.grant": "Kredi ver",
  "admin.users.make_admin": "Admin yap",
  "admin.users.revoke_admin": "Admin yetkisini kaldır",
  "admin.users.prompt_credits": "Kaç kredi eklensin?",

  "admin.products.title": "Ürünler",
  "admin.products.subtitle": "Aktif/pasif geçiş, bakım modu. Satışa hazırlık; kaynak + doküman + plan kontrolüne bağlıdır.",
  "admin.products.active": "aktif",
  "admin.products.inactive": "pasif",
  "admin.products.maint": "bakım",
  "admin.products.normal": "normal",
  "admin.products.sale_ready": "satışa hazır",
  "admin.products.pending": "yapılandırma bekliyor",
  "admin.products.refresh": "Hazırlık durumunu yenile",

  "admin.plans.title": "Planlar & fiyatlandırma",
  "admin.plans.subtitle": "Fiyat, kota ve hız limiti değerlerinin tamamı düzenlenebilir.",
  "admin.plans.col.product": "Ürün",
  "admin.plans.col.tier": "Katman",
  "admin.plans.col.price": "Fiyat",
  "admin.plans.col.reqs": "Aylık istek",
  "admin.plans.col.rate": "Hız/sn",
  "admin.plans.col.keys": "Anahtar",

  "admin.packs.title": "Kredi paketleri",
  "admin.packs.subtitle": "Eski sabit kredi paketleri (raporlama için tutuluyor; kullanıcılar özel tutarlı kredi yükler).",
  "admin.packs.col.name": "Ad",
  "admin.packs.col.credits": "Kredi",
  "admin.packs.col.price": "Fiyat",
  "admin.packs.col.popular": "Popüler",
  "admin.packs.col.active": "Aktif",

  "admin.incidents.title": "Olaylar & bakım",
  "admin.incidents.subtitle": "Kamuya açık durum sayfasına güncelleme yayınla.",
  "admin.incidents.form.title": "Başlık",
  "admin.incidents.form.affected": "Etkilenen servisler (virgülle ayrılmış slug)",
  "admin.incidents.form.body": "Güncelleme metni…",
  "admin.incidents.form.publish": "Olayı yayınla",
  "admin.incidents.resolve": "Çöz",
  "admin.incidents.empty": "Kayıtlı olay yok.",

  "admin.creem.title": "Creem ayarları",
  "admin.creem.subtitle": "Sunucu tarafında entegrasyon. API anahtarları şifreli saklanır ve frontend’e asla açık gönderilmez.",
  "admin.creem.mode": "Mod",
  "admin.creem.mode.test": "Test",
  "admin.creem.mode.live": "Canlı",
  "admin.creem.test_key": "Test API anahtarı",
  "admin.creem.live_key": "Canlı API anahtarı",
  "admin.creem.webhook_test": "Test webhook secret",
  "admin.creem.webhook_live": "Canlı webhook secret",
  "admin.creem.webhook_url": "Webhook URL",
  "admin.creem.success_url": "Başarı URL",
  "admin.creem.cancel_url": "İptal URL",
  "admin.creem.currency": "Varsayılan para birimi",
  "admin.creem.tax_mode": "Vergi modu",
  "admin.creem.tax_category": "Vergi kategorisi",
  "admin.creem.min_topup": "Minimum yükleme (USD)",
  "admin.creem.max_topup": "Maksimum yükleme (USD)",
  "admin.creem.rate": "USD → kredi oranı",
  "admin.creem.bonus": "Bonus katmanları (JSON)",
  "admin.creem.portal": "Müşteri portalını etkinleştir",
  "admin.creem.test_conn": "Bağlantıyı test et",
  "admin.creem.save": "Ayarları kaydet",
  "admin.creem.masked_notice": "Mevcut sır şifreli saklanır. Yeni değer yaz ya da değiştirmek istemiyorsan alanı boş bırak.",
  "admin.creem.status.test": "Test anahtarı",
  "admin.creem.status.live": "Canlı anahtar",
  "admin.creem.status.webhook_test": "Test webhook",
  "admin.creem.status.webhook_live": "Canlı webhook",
  "admin.creem.status.company": "Şirket bilgileri",
  "admin.creem.status.set": "Ayarlı",
  "admin.creem.status.missing": "Eksik",
  "admin.creem.status.complete": "Tamamlandı",
  "admin.creem.last_test": "Son başarılı test",
  "admin.creem.last_webhook": "Son webhook",
  "admin.creem.last_payment": "Son başarılı ödeme",
  "admin.creem.last_error": "Son entegrasyon hatası",

  "admin.resources.title": "API kaynakları",
  "admin.resources.subtitle": "Dış sağlayıcı yapılandırması. Tüm sırlar şifreli saklanır ve frontend’e asla iletilmez.",
  "admin.resources.add": "Kaynak ekle",
  "admin.resources.name": "Ad",
  "admin.resources.product": "API ürünü",
  "admin.resources.base_url": "Temel URL",
  "admin.resources.test_url": "Test URL",
  "admin.resources.api_key": "API anahtarı",
  "admin.resources.secret": "Secret",
  "admin.resources.username": "Kullanıcı adı",
  "admin.resources.password": "Şifre",
  "admin.resources.headers": "Ek başlıklar (JSON)",
  "admin.resources.timeout": "Timeout (sn)",
  "admin.resources.retries": "Yeniden deneme",
  "admin.resources.cache": "Cache (sn)",
  "admin.resources.daily": "Günlük kota",
  "admin.resources.monthly": "Aylık kota",
  "admin.resources.cost": "Sağlayıcı maliyeti (USD)",
  "admin.resources.commercial": "Ticari kullanım izni var",
  "admin.resources.resale": "Yeniden satış/dağıtım izni",
  "admin.resources.attribution": "Atıf metni (gerekliyse)",
  "admin.resources.license": "Lisans notu",
  "admin.resources.provider_key": "Sağlayıcı adaptör anahtarı",
  "admin.resources.active": "Aktif",
  "admin.resources.save": "Kaydet",
  "admin.resources.test": "Bağlantıyı test et",
  "admin.resources.delete": "Sil",
  "admin.resources.last_ok": "Son başarı",
  "admin.resources.last_err": "Son hata",
  "admin.resources.empty": "Henüz API kaynağı eklenmemiş. Bir kaynak ekleyerek ilgili API’yi satışa hazırla.",

  "admin.creem_products.title": "Creem ürün senkronizasyonu",
  "admin.creem_products.subtitle": "Her LoftPress planını ve kredi yükleme SKU’sunu Creem ürünüyle eşle.",
  "admin.creem_products.plan_col.product": "API",
  "admin.creem_products.plan_col.tier": "Katman",
  "admin.creem_products.plan_col.price": "Fiyat",
  "admin.creem_products.plan_col.test_id": "Creem ürün ID (test)",
  "admin.creem_products.plan_col.live_id": "Creem ürün ID (canlı)",
  "admin.creem_products.topup_title": "Kredi yükleme SKU’su",
  "admin.creem_products.list": "Creem ürünlerini getir",

  "admin.company.title": "Şirket bilgileri",
  "admin.company.subtitle": "Bu bilgiler tüm hukuki sayfa altbilgilerini, faturaları ve KVKK metnini besler. Hukuki metinleri yayımlamadan önce doldur.",
  "admin.company.legal_name": "Ticari unvan",
  "admin.company.number": "Şirket kayıt numarası",
  "admin.company.address": "Kayıtlı adres",
  "admin.company.country": "Ülke",
  "admin.company.support": "Destek e-postası",
  "admin.company.legal_email": "Hukuki bildirim e-postası",
  "admin.company.privacy": "Gizlilik / KVKK e-postası",
  "admin.company.phone": "Telefon",
  "admin.company.authorized": "Yetkili imzalayan",
  "admin.company.tax": "Vergi numarası",
  "admin.company.tax_office": "Vergi dairesi",
  "admin.company.refund_contact": "İade iletişim",
  "admin.company.website": "Web sitesi",

  "admin.legal.title": "Hukuki metinler",
  "admin.legal.subtitle": "İki dilli hukuki metinleri yönet. Değişikliği yayımla ve sürümle — onay kayıtları izlenebilir kalır.",
  "admin.legal.title_en": "Başlık (İngilizce)",
  "admin.legal.title_tr": "Başlık (Türkçe)",
  "admin.legal.body_en": "İçerik (İngilizce)",
  "admin.legal.body_tr": "İçerik (Türkçe)",
  "admin.legal.published": "Kamuya açık sitede yayında",
  "admin.legal.bump": "Sürümü artır (yeni snapshot; onayları yenilenmeye zorlar)",
  "admin.legal.save": "Kaydet",
  "admin.legal.versions": "Sürümler",
  "admin.legal.current_version": "Geçerli sürüm",

  "admin.consents.title": "Kullanıcı onay kayıtları",
  "admin.consents.subtitle": "Kabul edilen her hukuki belge değiştirilemez bir hash ile saklanır. Bu defter uyumluluk kanıtındır.",
  "admin.consents.col.user": "Kullanıcı",
  "admin.consents.col.doc": "Belge",
  "admin.consents.col.version": "Sürüm",
  "admin.consents.col.lang": "Dil",
  "admin.consents.col.context": "Bağlam",
  "admin.consents.col.ip": "IP",
  "admin.consents.col.at": "Kabul zamanı",

  "admin.refunds.title": "İadeler & itirazlar",
  "admin.refunds.subtitle": "Creem webhook’larından iletilen iade ve chargeback olayları.",
  "admin.refunds.col.at": "Tarih",
  "admin.refunds.col.user": "Kullanıcı",
  "admin.refunds.col.amount": "Tutar",
  "admin.refunds.col.status": "Durum",
  "admin.refunds.col.ref": "Creem ref",
  "admin.refunds.col.reason": "Sebep",
  "admin.refunds.empty": "Henüz iade kaydı yok.",

  "admin.webhooks.title": "Creem webhook olayları",
  "admin.webhooks.subtitle": "Gelen tüm Creem webhook’ları; imza durumu ve idempotency takibi.",
  "admin.webhooks.col.at": "Alındı",
  "admin.webhooks.col.type": "Olay türü",
  "admin.webhooks.col.id": "Olay ID",
  "admin.webhooks.col.verified": "İmza",
  "admin.webhooks.col.processed": "İşlendi",
  "admin.webhooks.col.error": "Hata",
  "admin.webhooks.reprocess": "Yeniden çalıştır",

  "common.copy": "Kopyala",
  "common.copied": "Kopyalandı",
  "common.copied_toast": "Panoya kopyalandı",
  "common.revoke": "İptal",
  "common.rotate": "Yenile",
  "common.create": "Oluştur",
  "common.save": "Kaydet",
  "common.cancel": "Vazgeç",
  "common.close": "Kapat",
  "common.upgrade": "Yükselt",
  "common.subscribe": "Abone ol",
  "common.select": "Seç",
  "common.loading": "Yükleniyor…",
  "common.buy": "Satın al",
  "common.download": "İndir",
  "common.yes": "evet",
  "common.no": "hayır",
  "common.saved": "Kaydedildi",
  "common.updated": "Güncellendi",
  "common.deleted": "Silindi",
  "common.error_generic": "Bir şeyler ters gitti. Lütfen tekrar dene.",
  "common.actions": "İşlemler",

  "err.404.title": "Sayfa bulunamadı",
  "err.404.body": "Aradığın sayfa mevcut değil.",
  "err.500.title": "Bizim tarafta bir şeyler kırıldı",
  "err.500.body": "İnceliyoruz. Lütfen birkaç dakika sonra tekrar dene.",
  "err.back_home": "Ana sayfaya dön",
};

export const DICTIONARIES = { en: EN, tr: TR };
